import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useBlocker } from 'react-router-dom';
import { generateAIPlan } from '@services/aiPlanGenerator';
import { addPublicPlan, pruneDemoPlans, type QuestionnaireData, type PublicPlan } from '@data/publicPlans';
import { createPlan, updatePlanContent, updatePlanStatus } from '@services/plans';
import { useAuth } from '@services/auth';
import { stylePresetsByKey } from '@data/stylePresets';
import type { AIGeneratedContent } from '@data/publicPlans';
import { useAlertDialog } from '../../shared/AlertDialog';
import BackButton from '../../shared/BackButton';
import { buildApiUrl } from '@utils/appBase';
import { getImageUrl } from '@utils/imageUrl';
import { generatePlanPDF } from '@utils/pdfGenerator';
import ImageModal from '../../shared/ImageModal';
import PlanGenerationPuzzleAnimation from '../../shared/PlanGenerationPuzzleAnimation';
import { getComponentLabel } from '@data/componentLabels';
import { useTranslatedAIContent } from '../../shared/useTranslatedAIContent';
import { useI18n } from '../../i18n';
import { trackActivityEvent, usePageActivityTracker } from '@services/activityTracker';
import {
	logGeneratePlanBootstrap,
	logPlanFlowEvent,
	resolvePendingQuestionnaireHandoff
} from '@services/planFlowDiagnostic';
import { getSkillLabel } from '@utils/skillLabels';
import GanttChart from '@components/GanttChart';
import { ganttTasksFromPlanInstructions, ganttTasksFromStructuredTimeline, generateSyntheticGanttData } from '@utils/ganttDataGenerator';
import {
	clearGeneratePlanReview,
	patchGeneratePlanReview,
	readGeneratePlanReview,
	writeGeneratePlanReview
} from '@utils/generatePlanReview';
import PlanTextEditor from '@components/plan/PlanTextEditor';
import PlanEditToolbar from '@components/plan/PlanEditToolbar';
import PlanTimelineEditor from '@components/plan/PlanTimelineEditor';
import PlanRequiredToolsList from '@components/plan/PlanRequiredToolsList';
import PlanIndicativePricingCard from '@components/plan/PlanIndicativePricingCard';
import PlanMaterialsQuantitiesCard from '@components/plan/PlanMaterialsQuantitiesCard';
import PlanTeamSkillsCard from '@components/plan/PlanTeamSkillsCard';
import ShareClientLinkModal from '@components/plan/ShareClientLinkModal';
import { getCompanyInviteForPlan, type CompanyPlanInviteStatus } from '@services/planInvites';
import { normalizeShareVisibility } from '@utils/shareVisibility';

const COMPONENT_TEXT_ALIASES: Record<string, string[]> = {
	'pool': ['pool', 'swimming pool', 'schwimmbecken', 'bazen'],
	'fence': ['fence', 'privacy screen', 'boundary wall', 'zaun', 'sichtschutz', 'holzzaun', 'ograda'],
	'bbq': ['bbq', 'barbecue', 'barbeque', 'grill', 'grillbereich', 'roštilj', 'rostilj'],
	'lounge': ['lounge', 'seating area', 'sitting area', 'outdoor seating', 'loungemöbel', 'loungebereich', 'sitzbereich', 'garnitur', 'sofa', 'sjedenje'],
	'outdoor-kitchen': ['outdoor kitchen', 'kitchen modules', 'counter set', 'aussenküche', 'außenküche', 'aussenkuche', 'outdoor-küche', 'grillküche', 'vanjska kuhinja'],
	'fire-pit': ['fire pit', 'fire feature', 'fireplace', 'feuerstelle', 'feuerschale', 'ognjište'],
	'pergola': ['pergola', 'gazebo', 'pavilion', 'überdachung', 'pavillon'],
	'deck': ['deck', 'decking', 'patio', 'terrace', 'holzterrasse', 'terrasse', 'terrassendielen', 'terasa'],
	'shed': ['shed', 'storage shed', 'garden shed', 'gartenhaus', 'gerätehaus', 'schuppen', 'vrtna kućica'],
	'playground': ['playground', 'play area', 'swing set', 'slide', 'spielplatz', 'schaukel', 'rutsche', 'igralište'],
	'vegetable-garden': ['vegetable garden', 'vegetable beds', 'raised bed', 'raised beds', 'hochbeet', 'hochbeete', 'gemüsebeet', 'gemüsegarten', 'kräuterbeet', 'povrtnjak', 'gredica'],
	'water-feature': ['water feature', 'fountain', 'ornamental pond', 'pond', 'waterfall', 'stream', 'rill', 'water basin', 'water bowl', 'springbrunnen', 'wasserspiel', 'wasserfall', 'teich', 'koiteich', 'koi-teich', 'fontana'],
	'lighting': ['lighting', 'light fixtures', 'path lights', 'spotlights', 'beleuchtung', 'außenleuchten', 'aussenleuchten', 'wegeleuchten', 'led-leuchten', 'gartenstrahler', 'rasvjeta', 'svjetiljka'],
	'pathway': ['pathway', 'walkway', 'path', 'paths', 'paving slabs', 'stepping stones', 'gartenweg', 'gehweg', 'weg', 'pfad', 'trittplatten', 'schieferplatten', 'pflasterweg', 'wegbelag', 'staza', 'kamena staza'],
	'retaining-wall': ['retaining wall', 'stützmauer', 'hangbefestigung', 'trockenmauer', 'potporni zid'],
	'driveway': ['driveway', 'parking area', 'parking space', 'einfahrt', 'zufahrt', 'stellplatz', 'prilaz'],
	'greenhouse': ['greenhouse', 'cold frame', 'gewächshaus', 'treibhaus', 'plastenik', 'staklenik'],
	'flower-beds': ['flower bed', 'flower beds', 'borders', 'planting bed', 'planted beds', 'perennial border', 'stauden', 'staudenbeet', 'blumenbeet', 'rabatte', 'bepflanzung', 'trajnice', 'cvjetna greda'],
	'hot-tub': ['hot tub', 'jacuzzi', 'spa tub', 'whirlpool', 'badetonne', 'spa-bereich', 'hidromasažna', 'hidromasazna'],
	'pizza-oven': ['pizza oven', 'wood-fired oven', 'pizzaofen', 'holzofen', 'peć za pizzu'],
	'compost': ['compost', 'compost area', 'compost bin', 'komposter', 'kompostbereich', 'kompostierung', 'kompostište']
};

function normalizeCoverageText(value: string): string {
	return value
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase();
}

function buildComponentCoverageText(ai: AIGeneratedContent): string {
	const chunks: string[] = [
		ai.planDescription,
		...(Array.isArray(ai.keyFeatures) ? ai.keyFeatures : []),
		...(Array.isArray(ai.instructions) ? ai.instructions : [])
	];

	if (Array.isArray(ai.timeline_tasks)) {
		ai.timeline_tasks.forEach((task) => {
			chunks.push(task.title, task.description);
		});
	}

	if (Array.isArray(ai.tools)) {
		ai.tools.forEach((tool) => {
			if (typeof tool === 'string') {
				chunks.push(tool);
			} else {
				chunks.push(tool.name, tool.description);
			}
		});
	}

	const materials = ai.pricingBreakdown?.materials;
	if (Array.isArray(materials)) {
		materials.forEach((item) => chunks.push(item.name));
	}

	const labor = ai.pricingBreakdown?.labor;
	if (Array.isArray(labor)) {
		labor.forEach((item) => chunks.push(item.name));
	}

	return normalizeCoverageText(chunks.filter(Boolean).join(' '));
}

function hasComponentTextEvidence(componentId: string, normalizedPlanText: string): boolean {
	const aliases = COMPONENT_TEXT_ALIASES[componentId] ?? [componentId.replace(/-/g, ' '), componentId];
	return aliases.some((alias) => {
		const needle = normalizeCoverageText(alias).trim();
		return needle.length > 0 && normalizedPlanText.includes(needle);
	});
}

function uniqueStrings(values: string[]): string[] {
	return Array.from(new Set(values));
}

// Helper function to format instruction text with highlights
function formatInstruction(instruction: string): JSX.Element {
	// Split by colon to separate title from description
	const colonIndex = instruction.indexOf(':');
	if (colonIndex === -1) {
		// No colon, return as-is with basic formatting
		return <span>{formatText(instruction)}</span>;
	}
	
	const title = instruction.substring(0, colonIndex);
	const description = instruction.substring(colonIndex + 1).trim();
	
	return (
		<>
			<span className="fw-bold" style={{ color: '#198754' }}>{formatText(title)}:</span>
			<span> {formatText(description)}</span>
		</>
	);
}

// Helper to format text with highlights for measurements, numbers, and key terms
function formatText(text: string): JSX.Element[] {
	const parts: JSX.Element[] = [];
	let lastIndex = 0;
	
	// Patterns to highlight:
	// 1. Timeframes: "Days 1-2", "Day 23", "24-48 hours"
	// 2. Measurements: "150-200mm", "300mm", "2-3 bags", "10-10-10"
	// 3. Percentages: "2%", "50%"
	// 4. Material names in parentheses
	// 5. Important action verbs
	
	const patterns = [
		// Timeframes
		/(Days? \d+-\d+|Day \d+|\d+-\d+ hours?)/gi,
		// Measurements with units
		/(\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?\s*(?:mm|cm|m|kg|bags?|pcs?|m²|m³|lm|times?|weeks?|months?))/gi,
		// Percentages
		/(\d+(?:\.\d+)?%)/gi,
		// Numbers followed by "x" (dimensions like "1m x 1m")
		/(\d+(?:\.\d+)?\s*x\s*\d+(?:\.\d+)?)/gi,
		// NPK ratios
		/(\d+-\d+-\d+)/g,
		// Material names in parentheses (e.g., "(bark chips, compost)")
		/(\([^)]+\))/g,
	];
	
	// Find all matches
	const matches: Array<{ start: number; end: number; text: string }> = [];
	
	patterns.forEach(pattern => {
		let match;
		while ((match = pattern.exec(text)) !== null) {
			matches.push({
				start: match.index,
				end: match.index + match[0].length,
				text: match[0]
			});
		}
	});
	
	// Sort matches by position
	matches.sort((a, b) => a.start - b.start);
	
	// Remove overlapping matches (keep first)
	const nonOverlapping: Array<{ start: number; end: number; text: string }> = [];
	for (const match of matches) {
		if (nonOverlapping.length === 0 || match.start >= nonOverlapping[nonOverlapping.length - 1].end) {
			nonOverlapping.push(match);
		}
	}
	
	// Build parts
	nonOverlapping.forEach((match, idx) => {
		// Add text before match
		if (match.start > lastIndex) {
			parts.push(<span key={`text-${idx}`}>{text.substring(lastIndex, match.start)}</span>);
		}
		// Determine highlight style based on content
		const isParentheses = match.text.startsWith('(');
		const isTimeframe = /Days?|hours?/i.test(match.text);
		const highlightClass = isParentheses 
			? "fw-semibold" 
			: isTimeframe 
				? "fw-bold"
				: "fw-bold";
		
		// Use green tones with different shades
		const highlightStyle: React.CSSProperties = isParentheses 
			? { color: '#198754', opacity: 0.8 } // Lighter green for parentheses
			: isTimeframe 
				? { color: '#157347' } // Darker green for timeframes
				: { color: '#198754' }; // Standard green for measurements
		
		// Add highlighted match
		parts.push(
			<span key={`highlight-${idx}`} className={highlightClass} style={highlightStyle}>
				{match.text}
			</span>
		);
		lastIndex = match.end;
	});
	
	// Add remaining text
	if (lastIndex < text.length) {
		parts.push(<span key="text-end">{text.substring(lastIndex)}</span>);
	}
	
	// If no matches, return original text
	if (parts.length === 0) {
		return [<span key="full">{text}</span>];
	}
	
	return parts;
}

// Helper to format key features with highlights
function formatFeatureText(feature: string): JSX.Element[] {
	const parts: JSX.Element[] = [];
	let lastIndex = 0;

	const featureStr = typeof feature === 'string' ? feature : String(feature || '');
	const matches: Array<{ start: number; end: number; text: string }> = [];
	const numberPattern = /(\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?\s*(?:mm|cm|m|kg|%|m²|m³|lm|pcs?|sets?))/gi;
	const gardenTermPattern =
		/\b(pergola|pavers?|planters?|lighting|bollards?|cedar|timber|grill|lawn|pathway|patio|fence|irrigation|drainage|soil|mulch|shrubs?|trees?|flowerbeds?|decking|seating)\b/gi;

	let match;
	while ((match = numberPattern.exec(featureStr)) !== null) {
		matches.push({ start: match.index, end: match.index + match[0].length, text: match[0] });
	}
	while ((match = gardenTermPattern.exec(featureStr)) !== null) {
		matches.push({ start: match.index, end: match.index + match[0].length, text: match[0] });
	}

	matches.sort((a, b) => a.start - b.start);
	const nonOverlapping: Array<{ start: number; end: number; text: string }> = [];
	for (const match of matches) {
		if (nonOverlapping.length === 0 || match.start >= nonOverlapping[nonOverlapping.length - 1].end) {
			nonOverlapping.push(match);
		}
	}

	nonOverlapping.forEach((match, idx) => {
		if (match.start > lastIndex) {
			parts.push(<span key={`text-${idx}`}>{featureStr.substring(lastIndex, match.start)}</span>);
		}
		parts.push(
			<span key={`highlight-${idx}`} className="fw-bold" style={{ color: '#198754' }}>
				{match.text}
			</span>
		);
		lastIndex = match.end;
	});
	
	if (lastIndex < featureStr.length) {
		parts.push(<span key="text-end">{featureStr.substring(lastIndex)}</span>);
	}
	
	if (parts.length === 0) {
		return [<span key="full">{featureStr}</span>];
	}
	
	return parts;
}

function toText(value: unknown, fallback = ''): string {
	if (typeof value === 'string') return value;
	if (value == null) return fallback;
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	try {
		return JSON.stringify(value);
	} catch {
		return fallback;
	}
}

function toTextList(value: unknown): string[] {
	if (!Array.isArray(value)) return [];
	return value.map((item) => toText(item).trim()).filter((item) => item.length > 0);
}

export default function AIGeneratedPlanView() {
	usePageActivityTracker('seeker_generate_plan');
	const navigate = useNavigate();
	const location = useLocation();
	const { showAlert } = useAlertDialog();
	const { user } = useAuth();
	const { language } = useI18n();
	const copy = useMemo(
		() => (en: string, de: string, hr: string) => (language === 'de' ? de : language === 'hr' ? hr : en),
		[language]
	);
	const languageRef = useRef(language);
	languageRef.current = language;
	const [loading, setLoading] = useState(true);
	const [questionnaire, setQuestionnaire] = useState<QuestionnaireData | null>(null);
	const [contactEmail, setContactEmail] = useState('');
	const [aiContent, setAiContent] = useState<AIGeneratedContent | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [showActionsInfo, setShowActionsInfo] = useState(false);
	const [imageModalOpen, setImageModalOpen] = useState(false);
	const [imageModalIndex, setImageModalIndex] = useState(0);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editInstructions, setEditInstructions] = useState('');
	const [isRegenerating, setIsRegenerating] = useState(false);
	const [generationElapsedSeconds, setGenerationElapsedSeconds] = useState(0);
	const [initialGenerationDurationSeconds, setInitialGenerationDurationSeconds] = useState<number | null>(null);
	const initialGenerationStartedAtRef = useRef<number | null>(null);
	const isCompanyFlow = user?.role === 'company';
	const [companySavedDatabaseId, setCompanySavedDatabaseId] = useState<number | null>(null);
	const [companyFrontendPlanId, setCompanyFrontendPlanId] = useState<string | null>(null);
	const [isPersistingPlan, setIsPersistingPlan] = useState(false);
	const [companyAutoSaveStatus, setCompanyAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');
	const [isSavingPlanText, setIsSavingPlanText] = useState(false);
	const [editablePlanContent, setEditablePlanContent] = useState<AIGeneratedContent | null>(null);
	const [activeTab, setActiveTab] = useState<'conceptual' | 'detailed' | 'project' | 'questionnaire'>('conceptual');
	const [isEditingPlan, setIsEditingPlan] = useState(false);
	const [companyInviteStatus, setCompanyInviteStatus] = useState<CompanyPlanInviteStatus | null>(null);
	const [shareClientModalOpen, setShareClientModalOpen] = useState(false);
	const companyAutoSaveAttemptedRef = useRef(false);
	const initialLoadHandledRef = useRef(false);
	/** Sync id for persist (state updates too late when auto-save + Continue to quote overlap). */
	const savedDatabaseIdRef = useRef<number | null>(null);
	const createPlanInFlightRef = useRef<Promise<number> | null>(null);
	const persistPlanQueueRef = useRef<Promise<void>>(Promise.resolve());

	const resolveSavedDatabaseId = useCallback((): number | null => {
		if (savedDatabaseIdRef.current != null) {
			return savedDatabaseIdRef.current;
		}
		const key = localStorage.getItem('currentDraftPlanId');
		if (key?.startsWith('db-')) {
			const n = parseInt(key.slice(3), 10);
			if (Number.isFinite(n) && n > 0) {
				savedDatabaseIdRef.current = n;
				return n;
			}
		}
		return null;
	}, []);

	const rememberSavedDatabaseId = useCallback((id: number) => {
		savedDatabaseIdRef.current = id;
		setCompanySavedDatabaseId(id);
		try {
			localStorage.setItem('currentDraftPlanId', `db-${id}`);
		} catch {
			/* ignore quota errors */
		}
	}, []);

	const planIdFromApiResponse = (plan: unknown): number | null => {
		if (!plan || typeof plan !== 'object') return null;
		const raw = (plan as Record<string, unknown>).id ?? (plan as Record<string, unknown>).plan_id;
		const n = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
		return Number.isFinite(n) && n > 0 ? n : null;
	};
	const { translatedContent: translatedAiContent } = useTranslatedAIContent(aiContent);
	const displayAi = translatedAiContent ?? aiContent;

	useEffect(() => {
		if (aiContent) {
			setEditablePlanContent(aiContent);
		}
	}, [aiContent]);

	const handleSaveCompanyPlanText = useCallback(async () => {
		if (!editablePlanContent) return;
		const dbId = resolveSavedDatabaseId();
		if (dbId == null) {
			showAlert(
				copy('Save the plan first, then you can update the text.', 'Speichern Sie zuerst den Plan, dann können Sie den Text aktualisieren.', 'Prvo spremite plan, zatim možete ažurirati tekst.'),
				{ title: copy('Not saved yet', 'Noch nicht gespeichert', 'Još nije spremljeno') }
			);
			return;
		}
		setIsSavingPlanText(true);
		try {
			await updatePlanContent(dbId, {
				ai_plan_details: editablePlanContent,
				after_image_url: editablePlanContent.photoAfterUrl || undefined
			});
			setAiContent(editablePlanContent);
			patchGeneratePlanReview({ aiContent: editablePlanContent });
			showAlert(
				copy('Plan updated. Clients see this version in the shared plan; your quote keeps its own copy when you build an offer.', 'Plan aktualisiert. Kunden sehen diese Version im geteilten Plan; Ihr Angebot behält eine eigene Kopie in der Angebotserstellung.', 'Plan je ažuriran. Klijenti vide ovu verziju u dijeljenom planu; vaša ponuda zadržava vlastitu kopiju pri izradi ponude.'),
				{ title: copy('Saved', 'Gespeichert', 'Spremljeno') }
			);
			setIsEditingPlan(false);
		} catch (err) {
			showAlert(err instanceof Error ? err.message : copy('Could not save changes.', 'Änderungen konnten nicht gespeichert werden.', 'Promjene nisu mogle biti spremljene.'), {
				title: copy('Save failed', 'Speichern fehlgeschlagen', 'Spremanje nije uspjelo')
			});
		} finally {
			setIsSavingPlanText(false);
		}
	}, [editablePlanContent, resolveSavedDatabaseId, copy, showAlert]);
	const handleDiscardCompanyPlanChanges = useCallback(() => {
		setEditablePlanContent(aiContent);
		setIsEditingPlan(false);
	}, [aiContent]);

	const companyUserId =
		user?.id != null ? (typeof user.id === 'number' ? user.id : parseInt(String(user.id), 10)) : null;
	const canEditCompanyPlan = isCompanyFlow && Boolean(editablePlanContent);
	const savedPlanDatabaseId = resolveSavedDatabaseId();
	const companyPlanForTimeline = useMemo((): PublicPlan | null => {
		if (!questionnaire || !editablePlanContent) return null;
		const dbId = resolveSavedDatabaseId();
		return {
			id: companyFrontendPlanId ?? (dbId != null ? `db-${dbId}` : 'company-draft'),
			databaseId: dbId ?? undefined,
			seekerUserId: String(user?.id ?? ''),
			title: questionnaire.location || copy('Garden plan', 'Gartenplan', 'Plan vrta'),
			city: questionnaire.location || '',
			contactEmail: contactEmail || '',
			styleKey: questionnaire.styleKey,
			status: 'draft',
			createdAt: new Date().toISOString(),
			questionnaire,
			aiGenerated: editablePlanContent,
			photoBeforeUrl: questionnaire.photoBeforeUrl
		};
	}, [
		questionnaire,
		editablePlanContent,
		resolveSavedDatabaseId,
		companyFrontendPlanId,
		user?.id,
		contactEmail,
		copy
	]);

	useEffect(() => {
		if (!isCompanyFlow || savedPlanDatabaseId == null || companyUserId == null) {
			setCompanyInviteStatus(null);
			return;
		}
		let cancelled = false;
		getCompanyInviteForPlan(savedPlanDatabaseId, companyUserId)
			.then((res) => {
				if (!cancelled) setCompanyInviteStatus(res);
			})
			.catch(() => {
				if (!cancelled) setCompanyInviteStatus(null);
			});
		return () => {
			cancelled = true;
		};
	}, [isCompanyFlow, savedPlanDatabaseId, companyUserId]);

	const getTabStyle = (tab: 'conceptual' | 'detailed' | 'project' | 'questionnaire') => ({
		color: activeTab === tab ? '#10b981' : '#6c757d',
		borderBottom: activeTab === tab ? '3px solid #10b981' : '3px solid transparent',
		fontWeight: activeTab === tab ? 600 : 400,
		backgroundColor: 'transparent',
		borderTop: 'none',
		borderLeft: 'none',
		borderRight: 'none',
		transition: 'all 0.2s ease'
	});

	const estimatedTimelineTasks = useMemo(() => {
		if (!displayAi || !questionnaire) return [];
		const startDate = questionnaire.projectStartDate ? new Date(questionnaire.projectStartDate) : new Date();
		const structured = ganttTasksFromStructuredTimeline(
			Array.isArray(displayAi.timeline_tasks) ? displayAi.timeline_tasks : [],
			startDate
		);
		if (structured.length > 0) return structured;

		const instructions = toTextList(displayAi.instructions);
		const planBased = ganttTasksFromPlanInstructions(instructions, startDate);
		if (planBased.length > 0) return planBased;

		const budgetMid = Number(displayAi.pricingBreakdown?.mid ?? 0);
		return budgetMid > 0 ? generateSyntheticGanttData('garden', budgetMid, startDate, undefined, undefined, language) : [];
	}, [displayAi, questionnaire, language]);

	const planGenerationInProgress = loading || isRegenerating;
	const unsavedGeneratedPlan = !error && aiContent !== null && !loading && !isRegenerating;
	const planSaved = companySavedDatabaseId != null || companyAutoSaveStatus === 'saved';
	const shouldBlockGeneratePlanExit =
		planGenerationInProgress || (unsavedGeneratedPlan && !planSaved);

	const generatePlanLeaveBlocker = useCallback(
		({ nextLocation }: { nextLocation: { pathname: string } }) => {
			if (!shouldBlockGeneratePlanExit) return false;
			const p = nextLocation.pathname;
			// Saving draft/publish navigates to plan detail — not an abandonment.
			if (unsavedGeneratedPlan && !planGenerationInProgress && p.startsWith('/app/seeker/plan/')) {
				const rest = p.slice('/app/seeker/plan/'.length);
				if (rest.length > 0 && !rest.includes('/')) return false;
			}
			if (isCompanyFlow && !planGenerationInProgress && p.startsWith('/app/plans/')) {
				return false;
			}
			return true;
		},
		[shouldBlockGeneratePlanExit, unsavedGeneratedPlan, planGenerationInProgress, isCompanyFlow, planSaved]
	);
	const leaveWhileGeneratingBlocker = useBlocker(generatePlanLeaveBlocker);

	useEffect(() => {
		if (!planGenerationInProgress) {
			setGenerationElapsedSeconds(0);
			return;
		}

		const startedAt = Date.now();
		setGenerationElapsedSeconds(0);
		const intervalId = window.setInterval(() => {
			const elapsed = Math.floor((Date.now() - startedAt) / 1000);
			setGenerationElapsedSeconds(elapsed);
		}, 1000);

		return () => window.clearInterval(intervalId);
	}, [planGenerationInProgress]);

	const formattedGenerationElapsed = `${String(Math.floor(generationElapsedSeconds / 60)).padStart(2, '0')}:${String(
		generationElapsedSeconds % 60
	).padStart(2, '0')}`;

	useEffect(() => {
		if (!shouldBlockGeneratePlanExit) return;
		const onBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = '';
		};
		window.addEventListener('beforeunload', onBeforeUnload);
		return () => window.removeEventListener('beforeunload', onBeforeUnload);
	}, [shouldBlockGeneratePlanExit]);

	useEffect(() => {
		if (leaveWhileGeneratingBlocker.state !== 'blocked') return;
		const message = planGenerationInProgress
			? copy(
				'Are you sure you want to leave? Plan generation in progress will be cancelled and you may lose progress.',
				'Sind Sie sicher, dass Sie die Seite verlassen möchten? Die laufende Plangenerierung wird abgebrochen und Ihr Fortschritt kann verloren gehen.',
				'Jeste li sigurni da želite izaći? Generiranje plana u tijeku bit će prekinuto i možete izgubiti napredak.'
			)
			: planSaved
				? isCompanyFlow
					? copy(
						'Leave this page? Your plan is saved as a draft in Direct plans. You can return anytime to build your quote.',
						'Seite verlassen? Ihr Plan ist als Entwurf unter „Direkte Pläne“ gespeichert. Sie können jederzeit zurückkehren, um Ihr Angebot zu erstellen.',
						'Napustiti stranicu? Plan je spremljen kao skica u „Izravni planovi“. Možete se vratiti bilo kada da izradite ponudu.'
					)
					: copy(
						'Leave this page? Your plan is saved as a draft in My Plans. You can return anytime to edit or publish it.',
						'Seite verlassen? Ihr Plan ist als Entwurf unter „Meine Pläne“ gespeichert. Sie können jederzeit zurückkehren, um ihn zu bearbeiten oder zu veröffentlichen.',
						'Napustiti stranicu? Plan je spremljen kao skica u „Mojim planovima“. Možete se vratiti bilo kada da ga uredite ili objavite.'
					)
				: copy(
					'Are you sure you want to leave? Your generated plan is not saved yet. Save as draft or publish first if you want to keep it.',
					'Sind Sie sicher, dass Sie die Seite verlassen möchten? Ihr generierter Plan ist noch nicht gespeichert. Speichern Sie zuerst als Entwurf oder veröffentlichen Sie ihn, wenn Sie ihn behalten möchten.',
					'Jeste li sigurni da želite izaći? Vaš generirani plan još nije spremljen. Prvo spremite kao skicu ili objavite ako ga želite zadržati.'
				);
		const shouldLeave = window.confirm(message);
		if (shouldLeave) {
			leaveWhileGeneratingBlocker.proceed();
		} else {
			leaveWhileGeneratingBlocker.reset();
		}
	}, [leaveWhileGeneratingBlocker.state, planGenerationInProgress]);

	// Invite code can be passed via navigation state from the questionnaire, or via query string
	const inviteCode: string | undefined = (() => {
		// Prefer navigation state
		const fromState = (location.state as any)?.inviteCode as string | undefined;
		if (fromState && typeof fromState === 'string' && fromState.trim()) {
			return fromState.trim();
		}
		// Fallback: query string (?invite=CODE or ?code=CODE)
		try {
			const params = new URLSearchParams(location.search);
			const raw = params.get('invite') || params.get('code');
			return raw && raw.trim() ? raw.trim() : undefined;
		} catch {
			return undefined;
		}
	})();

	const runGeneration = useCallback(
		(parsedQuestionnaire: QuestionnaireData, parsedContactEmail = '') => {
			setQuestionnaire(parsedQuestionnaire);
			setContactEmail(parsedContactEmail);
			setError(null);
			setAiContent(null);
			setLoading(true);
			initialGenerationStartedAtRef.current = Date.now();
			setInitialGenerationDurationSeconds(null);

			const genLanguage =
				languageRef.current === 'de' ? 'de' : languageRef.current === 'hr' ? 'hr' : 'en';
			logPlanFlowEvent('ai_generation_request', 'plan_generation_started', {
				language: genLanguage,
				user_id: user?.id ?? null,
				style_key: parsedQuestionnaire.styleKey,
				location_len: String(parsedQuestionnaire.location ?? '').length,
				photo_before_len: String(parsedQuestionnaire.photoBeforeUrl ?? '').length
			});
			generateAIPlan(parsedQuestionnaire, {
				userId: user?.id,
				language: genLanguage
			})
				.then((content) => {
					if (initialGenerationStartedAtRef.current) {
						const durationSeconds = Math.max(
							0,
							Math.floor((Date.now() - initialGenerationStartedAtRef.current) / 1000)
						);
						setInitialGenerationDurationSeconds(durationSeconds);
					}
					setAiContent(content);
					try {
						localStorage.removeItem('pendingQuestionnaire');
						writeGeneratePlanReview({
							questionnaire: parsedQuestionnaire,
							aiContent: content,
							contactEmail: parsedContactEmail
						});
					} catch (storageErr) {
						console.warn('Failed to persist review session after generation:', storageErr);
					}
					void trackActivityEvent({
						event_name: 'plan_generation_success',
						properties: {
							duration_seconds: initialGenerationStartedAtRef.current
								? Math.max(0, Math.floor((Date.now() - initialGenerationStartedAtRef.current) / 1000))
								: undefined
						}
					});
					setLoading(false);
				})
				.catch((err) => {
					const lang = languageRef.current;
					const errorMessage =
						err instanceof Error
							? err.message
							: lang === 'de'
								? 'Plan konnte nicht erstellt werden. Bitte versuchen Sie es erneut.'
								: lang === 'hr'
									? 'Generiranje plana nije uspjelo. Pokušajte ponovno.'
									: 'Failed to generate plan. Please try again.';
					if (initialGenerationStartedAtRef.current) {
						const durationSeconds = Math.max(
							0,
							Math.floor((Date.now() - initialGenerationStartedAtRef.current) / 1000)
						);
						setInitialGenerationDurationSeconds(durationSeconds);
					}
					setError(errorMessage);
					console.error('AI generation error:', err);
					const errDetails =
						err instanceof Error
							? { message: err.message.slice(0, 500), name: err.name }
							: { message: String(err).slice(0, 500) };
					logPlanFlowEvent('ai_generation_response', 'plan_generation_failed', {
						...errDetails,
						language: languageRef.current
					});
					void trackActivityEvent({
						event_name: 'plan_generation_error',
						properties: { ...errDetails, user_message: errorMessage }
					});
					setLoading(false);
				});
		},
		[user?.id]
	);

	useEffect(() => {
		if (initialLoadHandledRef.current) return;
		initialLoadHandledRef.current = true;

		const draftPlanDatabaseId = (() => {
			const fromState = (location.state as { draftPlanDatabaseId?: number } | null)?.draftPlanDatabaseId;
			if (typeof fromState === 'number' && Number.isFinite(fromState) && fromState > 0) {
				return fromState;
			}
			const key = localStorage.getItem('currentDraftPlanId');
			if (!key?.startsWith('db-')) return null;
			const id = parseInt(key.slice(3), 10);
			return Number.isFinite(id) && id > 0 ? id : null;
		})();
		if (draftPlanDatabaseId != null) {
			rememberSavedDatabaseId(draftPlanDatabaseId);
		}

		const restoreReview = readGeneratePlanReview();
		if (restoreReview?.questionnaire && restoreReview.aiContent) {
			logGeneratePlanBootstrap({ source: 'review_session' });
			setQuestionnaire(restoreReview.questionnaire);
			setContactEmail(restoreReview.contactEmail || '');
			setAiContent(restoreReview.aiContent);
			if (restoreReview.databaseId) {
				rememberSavedDatabaseId(restoreReview.databaseId);
			}
			if (restoreReview.frontendPlanId) {
				setCompanyFrontendPlanId(restoreReview.frontendPlanId);
			}
			if (restoreReview.databaseId) {
				setCompanyAutoSaveStatus('saved');
				companyAutoSaveAttemptedRef.current = true;
			}
			setError(null);
			setLoading(false);
			return;
		}

		const handoff = resolvePendingQuestionnaireHandoff(location.state);
		if (!handoff) {
			logGeneratePlanBootstrap({
				source: 'none',
				reason: 'no_pending_questionnaire',
				errorCode: 'missing'
			});
			setError(
				copy(
					'No questionnaire data found. Please start over.',
					'Keine Fragebogen-Daten gefunden. Bitte starten Sie von vorn.',
					'Nisu pronađeni podaci upitnika. Započnite ispočetka.'
				)
			);
			setLoading(false);
			return;
		}

		try {
			const q = handoff.data.questionnaire;
			if (!q || typeof q !== 'object') {
				throw new Error('Missing questionnaire');
			}
			logGeneratePlanBootstrap({ source: handoff.source });
			if (handoff.source === 'navigation_state') {
				try {
					localStorage.setItem('pendingQuestionnaire', JSON.stringify(handoff.data));
				} catch (persistErr) {
					logPlanFlowEvent('generate_plan_bootstrap', 'persist_handoff_failed', {
						error: persistErr instanceof Error ? persistErr.message : String(persistErr)
					});
				}
			}
			runGeneration(q as QuestionnaireData, handoff.data.contactEmail || '');
		} catch (err) {
			logGeneratePlanBootstrap({
				source: 'none',
				reason: err instanceof Error ? err.message : String(err),
				errorCode: 'invalid_json'
			});
			setError(
				copy(
					'Invalid questionnaire data. Please start over.',
					'Ungültige Fragebogen-Daten. Bitte starten Sie von vorn.',
					'Neispravni podaci upitnika. Započnite ispočetka.'
				)
			);
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap once on mount
	}, [runGeneration]);

	function handleRetryGeneration() {
		const handoff = resolvePendingQuestionnaireHandoff(location.state);
		if (!handoff) {
			logPlanFlowEvent('generate_plan_retry', 'missing_no_questionnaire', {});
			setError(
				copy(
					'No questionnaire data found. Please go back to the questionnaire.',
					'Keine Fragebogen-Daten gefunden. Bitte kehren Sie zum Fragebogen zurück.',
					'Nisu pronađeni podaci upitnika. Vratite se na upitnik.'
				)
			);
			return;
		}

		try {
			const q = handoff.data.questionnaire;
			if (!q || typeof q !== 'object') {
				throw new Error('Missing questionnaire');
			}
			logPlanFlowEvent('generate_plan_retry', 'retry_started', { source: handoff.source });
			runGeneration(q as QuestionnaireData, handoff.data.contactEmail || '');
		} catch (err) {
			console.error('Failed to reload questionnaire for retry:', err);
			logPlanFlowEvent('generate_plan_retry', 'retry_invalid_questionnaire', {
				error: err instanceof Error ? err.message : String(err)
			});
			setError(
				copy(
					'Could not reload your questionnaire. Please go back and review it.',
					'Fragebogen konnte nicht erneut geladen werden. Bitte kehren Sie zurück und prüfen Sie ihn.',
					'Upitnik se nije mogao ponovno učitati. Vratite se i provjerite ga.'
				)
			);
		}
	}

	async function handleSaveQuestionnaireDraft() {
		if (!questionnaire) {
			showAlert(copy('Questionnaire data is not available to save.', 'Fragebogendaten sind zum Speichern nicht verfügbar.', 'Podaci upitnika nisu dostupni za spremanje.'), { title: copy('Save failed', 'Speichern fehlgeschlagen', 'Spremanje nije uspjelo') });
			return;
		}

		try {
			const draftData: Record<string, unknown> = {
				form: questionnaire,
				contactEmail,
				currentStep: 5
			};
			if (questionnaire.photoBeforeUrl) {
				draftData.photoBeforeUrl = questionnaire.photoBeforeUrl;
			}
			localStorage.setItem('gardenQuestionnaireDraft', JSON.stringify(draftData));

			// Also persist a real draft plan when user is logged in (update existing row if we already have one).
			let createdDraftInPlans = false;
			const numericUserId = user?.id ? Number(user.id) : NaN;
			if (!Number.isNaN(numericUserId) && numericUserId > 0) {
				const beforeImageUrl =
					questionnaire.photoBeforeUrl && !questionnaire.photoBeforeUrl.startsWith('data:')
						? questionnaire.photoBeforeUrl
						: undefined;
				const effectiveContact = (contactEmail || user?.email || '').trim() || undefined;

				const draftTitle = `${stylePresetsByKey[questionnaire.styleKey]?.title} Garden - ${questionnaire.location}`;
				const existingDbId =
					companySavedDatabaseId ??
					(() => {
						const key = localStorage.getItem('currentDraftPlanId');
						if (!key?.startsWith('db-')) return null;
						const n = parseInt(key.slice(3), 10);
						return Number.isFinite(n) && n > 0 ? n : null;
					})();

				try {
					if (existingDbId != null) {
						await updatePlanContent(existingDbId, {
							title: draftTitle,
							description: questionnaire.needsWants || undefined,
							location: questionnaire.location,
							style: questionnaire.styleKey,
							garden_size: questionnaire.gardenSize || undefined,
							garden_shape: questionnaire.gardenShape || undefined,
							garden_components:
								questionnaire.selectedComponents && questionnaire.selectedComponents.length > 0
									? JSON.stringify(questionnaire.selectedComponents)
									: undefined,
							needs_wants: questionnaire.needsWants || undefined,
							maintenance_level: questionnaire.maintenanceLevel || undefined,
							children_pets: questionnaire.childrenPets || undefined,
							budget_min: questionnaire.budgetRange?.min || undefined,
							budget_max: questionnaire.budgetRange?.max || undefined,
							timeline: questionnaire.timeline || undefined,
							project_start_date: questionnaire.projectStartDate || undefined,
							contact_email: effectiveContact,
							before_image_url: beforeImageUrl,
							after_image_url: beforeImageUrl,
							ai_questions: (((questionnaire as any).aiAllQuestions ?? questionnaire.aiAnalysis?.suggestedQuestions) as any) || undefined,
							ai_answers: {
								...(questionnaire.aiAnswers || {}),
								...(questionnaire.aiQuestionComments && Object.keys(questionnaire.aiQuestionComments).length > 0
									? Object.fromEntries(
											Object.entries(questionnaire.aiQuestionComments).map(([k, v]) => [`ai_q_${k}_comment`, v])
										)
									: {})
							},
							ai_analysis: questionnaire.aiAnalysis || undefined
						});
						rememberSavedDatabaseId(existingDbId);
					} else {
						const backendPlan = await createPlan({
							user_id: numericUserId,
							title: draftTitle,
							description: questionnaire.needsWants || undefined,
							location: questionnaire.location,
							style: questionnaire.styleKey,
							garden_size: questionnaire.gardenSize || undefined,
							garden_shape: questionnaire.gardenShape || undefined,
							garden_components: questionnaire.selectedComponents && questionnaire.selectedComponents.length > 0
								? JSON.stringify(questionnaire.selectedComponents)
								: undefined,
							needs_wants: questionnaire.needsWants || undefined,
							maintenance_level: questionnaire.maintenanceLevel || undefined,
							children_pets: questionnaire.childrenPets || undefined,
							budget_min: questionnaire.budgetRange?.min || undefined,
							budget_max: questionnaire.budgetRange?.max || undefined,
							timeline: questionnaire.timeline || undefined,
							project_start_date: questionnaire.projectStartDate || undefined,
							contact_email: effectiveContact,
							before_image_url: beforeImageUrl,
							after_image_url: beforeImageUrl,
							status: 'draft',
							plan_type: 'professional',
							source: user?.role === 'company' ? ('invite' as const) : undefined,
							visibility: user?.role === 'company' ? ('private' as const) : undefined,
							ai_questions: (((questionnaire as any).aiAllQuestions ?? questionnaire.aiAnalysis?.suggestedQuestions) as any) || undefined,
							ai_answers: {
								...(questionnaire.aiAnswers || {}),
								...(questionnaire.aiQuestionComments && Object.keys(questionnaire.aiQuestionComments).length > 0
									? Object.fromEntries(
											Object.entries(questionnaire.aiQuestionComments).map(([k, v]) => [`ai_q_${k}_comment`, v])
										)
									: {})
							},
							ai_analysis: questionnaire.aiAnalysis || undefined
						});
						const newDraftId = planIdFromApiResponse(backendPlan);
						if (newDraftId == null) {
							throw new Error('Plan was created but the server did not return a plan id.');
						}
						rememberSavedDatabaseId(newDraftId);

						addPublicPlan({
							id: `pl${Math.random().toString(36).slice(2, 8)}`,
							databaseId: backendPlan.id,
							seekerUserId: String(numericUserId),
							title: backendPlan.title,
							city: backendPlan.location || questionnaire.location,
							contactEmail: effectiveContact || '',
							styleKey: questionnaire.styleKey,
							photoBeforeUrl: beforeImageUrl,
							status: 'draft',
							createdAt: new Date().toISOString(),
							questionnaire
						});
					}

					createdDraftInPlans = true;
				} catch (apiErr) {
					console.error('Failed to create draft plan from error state:', apiErr);
				}
			}

			showAlert(
				createdDraftInPlans
					? copy('Your questionnaire was saved as a draft plan in My plans.', 'Ihr Fragebogen wurde als Entwurfsplan in "Meine Pläne" gespeichert.', 'Vaš upitnik je spremljen kao skica plana u "Moji planovi".')
					: copy('Your questionnaire answers were saved. You can come back and try again anytime.', 'Ihre Antworten aus dem Fragebogen wurden gespeichert. Sie können jederzeit zurückkehren und es erneut versuchen.', 'Vaši odgovori iz upitnika su spremljeni. Možete se vratiti i pokušati ponovno u bilo kojem trenutku.'),
				{ title: copy('Draft saved', 'Entwurf gespeichert', 'Skica spremljena') }
			);
			navigate(createdDraftInPlans ? '/app/seeker/plans?tab=drafts' : '/app/seeker/create-plan');
		} catch (err) {
			console.error('Failed to save questionnaire draft:', err);
			showAlert(copy('Could not save your questionnaire draft. Please try again.', 'Ihr Fragebogen-Entwurf konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.', 'Nije moguće spremiti skicu upitnika. Pokušajte ponovno.'), { title: copy('Save failed', 'Speichern fehlgeschlagen', 'Spremanje nije uspjelo') });
		}
	}

// Helper function to convert data URL to File
function dataURLtoFile(dataurl: string, filename: string): File {
	const arr = dataurl.split(',');
	const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
	const bstr = atob(arr[1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);
	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}
	return new File([u8arr], filename, { type: mime });
}

type PersistPlanOptions = {
	navigateTo?: 'plan' | 'offer' | 'none';
	silent?: boolean;
};

async function persistPlan(
	status: 'draft' | 'active',
	successMessage: string,
	successTitle: string,
	options?: PersistPlanOptions
) {
	if (!questionnaire || !aiContent) {
		showAlert(copy('Plan data is missing. Please refresh or start again from the questionnaire.', 'Plandaten fehlen. Bitte aktualisieren Sie die Seite oder starten Sie erneut vom Fragebogen.', 'Nedostaju podaci plana. Osvježite stranicu ili krenite ponovno iz upitnika.'), { title: copy('Cannot save', 'Speichern nicht möglich', 'Spremanje nije moguće') });
		return;
	}

	const runPersist = async () => {
	setIsPersistingPlan(true);
	const silentSave = Boolean(options?.silent);
	// Questionnaire often hides the email field; fall back to the signed-in user’s email.
	const effectiveContact = (contactEmail || user?.email || '').trim();
	if (!effectiveContact) {
		setIsPersistingPlan(false);
		showAlert(
			copy('We need an email to save your plan. Add one in Settings, or go back to the questionnaire and complete the contact step.', 'Wir benötigen eine E-Mail-Adresse, um Ihren Plan zu speichern. Fügen Sie sie in den Einstellungen hinzu oder gehen Sie zurück zum Fragebogen und schließen Sie den Kontakt-Schritt ab.', 'Za spremanje plana potrebna je e-mail adresa. Dodajte je u postavkama ili se vratite u upitnik i dovršite korak kontakta.'),
			{ title: copy('Email required', 'E-Mail erforderlich', 'E-mail je obavezan') }
		);
		return;
	}

	// Upload photo if it's a data URL
	let uploadedPhotoUrl: string | undefined;
	if (questionnaire.photoBeforeUrl && questionnaire.photoBeforeUrl.startsWith('data:')) {
		try {
			// Convert data URL to File
			const file = dataURLtoFile(questionnaire.photoBeforeUrl, 'photo.jpg');
			
			const formData = new FormData();
			formData.append('image', file);
			formData.append('type', 'plan');
			formData.append('subtype', 'before');
			
			const response = await fetch(buildApiUrl('/upload'), {
				method: 'POST',
				body: formData
			});
			
			
			if (response.ok) {
				// Try to parse JSON, but handle invalid JSON gracefully
				let data;
				const contentType = response.headers.get('content-type');
				if (contentType && contentType.includes('application/json')) {
					try {
						data = await response.json();
					} catch (jsonErr) {
						// If JSON parsing fails, read as text to see what we got
						const textResponse = await response.text();
						console.error('❌ Invalid JSON response from upload:', textResponse.substring(0, 200));
						throw new Error('Server returned invalid JSON response');
					}
				} else {
					// Not JSON, read as text
					const textResponse = await response.text();
					console.error('❌ Non-JSON response from upload:', textResponse.substring(0, 200));
					throw new Error('Server returned non-JSON response');
				}
				
				if (data.success && data.data?.url) {
					const relativeUrl = data.data.url;
					// Save relative URL to database (not absolute URL with localhost)
					// The imageUrl utility will handle converting it to the correct absolute URL based on environment
					uploadedPhotoUrl = relativeUrl;
					if (uploadedPhotoUrl) {
					}
				} else {
					const errorMsg = data.error || 'Unknown error';
					console.error('❌ Photo upload failed:', errorMsg, data);
					// Don't throw - we'll continue without the uploaded photo
				}
			} else {
				// Try to get error details
				let errorText = '';
				let errorDetails = null;
				try {
					const contentType = response.headers.get('content-type');
					if (contentType && contentType.includes('application/json')) {
						errorDetails = await response.json();
						errorText = errorDetails.error || 'Unknown error';
						console.error('❌ Upload failed with status:', response.status);
						console.error('❌ Error:', errorText);
						if (errorDetails.details) {
							console.error('❌ Error details:', errorDetails.details);
						}
					} else {
						errorText = await response.text();
						console.error('❌ Upload failed with status:', response.status, 'Response:', errorText.substring(0, 500));
					}
				} catch (parseErr) {
					errorText = await response.text();
					console.error('❌ Upload failed with status:', response.status, 'Response:', errorText.substring(0, 500));
				}
				// Don't throw - we'll continue without the uploaded photo
			}
		} catch (uploadErr) {
			console.error('❌ Exception during photo upload:', uploadErr);
			if (uploadErr instanceof Error) {
				console.error('Error message:', uploadErr.message);
			}
			// Don't throw - we'll continue without the uploaded photo
		}
	} else if (questionnaire.photoBeforeUrl) {
		// Already a URL, use it as-is
		uploadedPhotoUrl = questionnaire.photoBeforeUrl;
	}

	const id = companyFrontendPlanId ?? `pl${Math.random().toString(36).slice(2, 8)}`;
	
	// Get user ID for database (need this before deciding on photo storage)
	const userId = user?.id;
	
	if (!user) {
		console.warn('⚠️ User not logged in - plan will only be saved to localStorage');
	}
	
	let userIdNum: number | undefined;
	if (typeof userId === 'string') {
		const parsed = parseInt(userId, 10);
		userIdNum = !isNaN(parsed) && parsed > 0 ? parsed : undefined;
	} else if (typeof userId === 'number') {
		userIdNum = userId > 0 ? userId : undefined;
	} else {
		console.warn('⚠️ No user ID available or user not logged in');
	}
	
	const isNumericUserId = userIdNum !== undefined && !isNaN(userIdNum) && userIdNum > 0;
	
	// For localStorage: if we have an uploaded URL, use it. Otherwise, only use data URL if NOT saving to database
	// This prevents filling up localStorage when database save succeeds
	let photoUrlForStorage: string | undefined;
	if (uploadedPhotoUrl) {
		// Use uploaded URL - this is small and won't fill localStorage
		photoUrlForStorage = uploadedPhotoUrl;
	} else if (!isNumericUserId) {
		// Keep original data URL for demo mode (no client-side recompression).
		photoUrlForStorage = questionnaire.photoBeforeUrl;
	}
	// If saving to database and no uploaded URL, don't store photo in localStorage
	
	const optimizedQuestionnaire: QuestionnaireData = {
		...questionnaire,
		photoBeforeUrl: photoUrlForStorage || questionnaire.photoBeforeUrl
	};

	// Try to save to database (both drafts and published plans)
	let databaseId: number | undefined;
	let databaseSaveSucceeded = false;
	if (isNumericUserId && userIdNum !== undefined) {
		try {
			// Determine image URLs
			// before_image_url = user's uploaded photo (the "before" photo they took/uploaded)
			// after_image_url = AI-generated transformed image (the "after" vision)
			// For now, if there's no real AI-generated image (only placeholder Unsplash URLs), use the same uploaded photo for both sides
			const beforeImageUrl = uploadedPhotoUrl || 
				(questionnaire.photoBeforeUrl && !questionnaire.photoBeforeUrl.startsWith('data:') 
					? questionnaire.photoBeforeUrl 
					: undefined);
			
			// Check if photoAfterUrl is a placeholder (Unsplash) or a real AI-generated image
			// If it's from Unsplash, it's just a placeholder - use the uploaded photo instead
			const isPlaceholderImage = aiContent?.photoAfterUrl?.includes('unsplash.com');
			const afterImageUrl = (isPlaceholderImage ? undefined : aiContent?.photoAfterUrl) || beforeImageUrl || undefined;
			
			
			const planData = {
				user_id: userIdNum,
				title: `${stylePresetsByKey[questionnaire.styleKey]?.title} Garden - ${questionnaire.location}`,
				description: questionnaire.needsWants || undefined,
				location: questionnaire.location,
				style: questionnaire.styleKey,
				// Questionnaire fields - save to database columns
				garden_size: questionnaire.gardenSize || undefined,
				garden_shape: questionnaire.gardenShape || undefined,
				garden_components: questionnaire.selectedComponents && questionnaire.selectedComponents.length > 0 
					? JSON.stringify(questionnaire.selectedComponents) 
					: undefined,
				needs_wants: questionnaire.needsWants || undefined,
				maintenance_level: questionnaire.maintenanceLevel || undefined,
				children_pets: questionnaire.childrenPets || undefined,
				budget_min: questionnaire.budgetRange?.min || undefined,
				budget_max: questionnaire.budgetRange?.max || undefined,
				timeline: questionnaire.timeline || undefined,
				project_start_date: questionnaire.projectStartDate || undefined,
				contact_email: effectiveContact,
				// Plan metadata
				before_image_url: beforeImageUrl,  // User's uploaded photo
				after_image_url: afterImageUrl,    // AI-generated transformed image
				status: (status === 'active' ? 'published' : 'draft') as 'draft' | 'published' | 'closed' | 'archived', // Map 'active' to 'published', 'draft' stays 'draft'
				plan_type: 'professional' as 'professional' | 'diy',
				source: user?.role === 'company' ? 'invite' as const : undefined,
				visibility: user?.role === 'company' ? 'private' as const : undefined,
				// AI-generated content only (not questionnaire data)
				ai_plan_details: {
					aiGenerated: aiContent
				},
				generation_duration_seconds: initialGenerationDurationSeconds ?? undefined,
				// AI Questionnaire data:
				// - `ai_questions` is used for display of smart-question answers (needs the full combined list so indices match `ai_answers`).
				// - `ai_analysis.suggestedQuestions` is still used by the backend for prompt-generation, but planData.ai_questions is independent.
				ai_questions: (((questionnaire as any).aiAllQuestions ?? questionnaire.aiAnalysis?.suggestedQuestions) as any) || undefined,
				ai_answers: {
					...(questionnaire.aiAnswers || {}),
					...(questionnaire.aiQuestionComments && Object.keys(questionnaire.aiQuestionComments).length > 0
						? Object.fromEntries(
								Object.entries(questionnaire.aiQuestionComments).map(([k, v]) => [`ai_q_${k}_comment`, v])
							)
						: {})
				},
				ai_analysis: questionnaire.aiAnalysis || undefined
			};

			const contentUpdatePayload = {
				title: planData.title,
				description: planData.description,
				style: planData.style,
				location: planData.location,
				garden_size: planData.garden_size,
				garden_shape: planData.garden_shape,
				garden_components: planData.garden_components,
				needs_wants: planData.needs_wants,
				maintenance_level: planData.maintenance_level,
				children_pets: planData.children_pets,
				budget_min: planData.budget_min,
				budget_max: planData.budget_max,
				timeline: planData.timeline,
				project_start_date: planData.project_start_date,
				contact_email: planData.contact_email,
				before_image_url: planData.before_image_url,
				after_image_url: planData.after_image_url,
				ai_plan_details: planData.ai_plan_details,
				ai_questions: planData.ai_questions,
				ai_answers: planData.ai_answers,
				ai_analysis: planData.ai_analysis
			};

			let resolvedPlanId = resolveSavedDatabaseId();
			if (resolvedPlanId == null) {
				if (!createPlanInFlightRef.current) {
					createPlanInFlightRef.current = createPlan({
						...planData,
						invite_code: inviteCode || undefined
					})
						.then((backendPlan) => {
							const newId = planIdFromApiResponse(backendPlan);
							if (newId == null) {
								throw new Error('Plan was created but the server did not return a plan id.');
							}
							rememberSavedDatabaseId(newId);
							if (isCompanyFlow) {
								setCompanyFrontendPlanId(id);
							}
							return newId;
						})
						.finally(() => {
							createPlanInFlightRef.current = null;
						});
				}
				resolvedPlanId = await createPlanInFlightRef.current;
				// createPlan payload already includes questionnaire + AI content — no follow-up PUT needed
			} else {
				await updatePlanContent(resolvedPlanId, contentUpdatePayload);
			}
			if (status === 'active') {
				await updatePlanStatus(resolvedPlanId, 'published');
			}
			databaseId = resolvedPlanId;

			databaseSaveSucceeded = true;
			if (databaseId) {
				rememberSavedDatabaseId(databaseId);
				patchGeneratePlanReview({
					databaseId,
					frontendPlanId: id,
					questionnaire: optimizedQuestionnaire,
					aiContent
				});
			}
			
			// Create minimal plan for localStorage so PlanDetails can find it by frontend ID
			// Make sure photoBeforeUrl is included in questionnaire
			const questionnaireWithPhoto = {
				...optimizedQuestionnaire,
				photoBeforeUrl: uploadedPhotoUrl || optimizedQuestionnaire.photoBeforeUrl
			};
			
			const plan: PublicPlan = {
				id,
				databaseId,
				seekerUserId: userId ? String(userId) : 'u-seeker-demo',
				title: `${stylePresetsByKey[questionnaire.styleKey]?.title} Garden - ${questionnaire.location}`,
				city: questionnaire.location,
				contactEmail: effectiveContact,
				styleKey: questionnaire.styleKey,
				photoBeforeUrl: uploadedPhotoUrl || undefined,
				status,
				createdAt: new Date().toISOString(),
				questionnaire: questionnaireWithPhoto,
				aiGenerated: aiContent
			};
			
			// Save minimal plan to localStorage so frontend can find it
			try {
				addPublicPlan(plan);
			} catch (err) {
				console.warn('Failed to save plan to localStorage (non-critical):', err);
			}
			
			localStorage.removeItem('pendingQuestionnaire');
			localStorage.removeItem('gardenQuestionnaireDraft');
			const companyDraftHint =
				isCompanyFlow && status === 'draft' && !options?.silent
					? '\n\n' +
						copy(
							'Tip: open Direct plans in the sidebar to invite clients or return to build your quote.',
							'Tipp: Unter „Direkte Pläne“ in der Seitenleiste können Sie Kunden einladen oder Ihr Angebot erstellen.',
							'Savjet: u izborniku „Izravni planovi“ možete pozvati klijente ili nastaviti s izradom ponude.'
						)
					: '';
			if (!options?.silent) {
				showAlert(successMessage + companyDraftHint, { title: successTitle });
			} else {
				setCompanyAutoSaveStatus('saved');
			}
			const planIdForNavigation = databaseId ? `db-${databaseId}` : id;
			const navigateTo = options?.navigateTo ?? 'plan';
			setIsPersistingPlan(false);
			if (navigateTo === 'offer') {
				navigate(`/app/plans/${planIdForNavigation}`, {
					state: { returnTo: '/app/seeker/generate-plan' }
				});
			} else if (navigateTo !== 'none') {
				navigate(`/app/seeker/plan/${planIdForNavigation}`);
			}
			return; // Exit early - don't try full localStorage save
		} catch (apiErr) {
			console.error('❌ Failed to create plan in database:', apiErr);
			if (apiErr instanceof Error) {
				console.error('Error message:', apiErr.message);
				console.error('Error stack:', apiErr.stack);
			}
			// Show error to user - don't fall back to localStorage
			const errorMsg = apiErr instanceof Error ? apiErr.message : 'Unknown error';
			const isDraft = status === 'draft';
			
			// Determine the error message based on the error type
			let userMessage = copy('Publishing the plan failed. Please try again.', 'Die Veröffentlichung des Plans ist fehlgeschlagen. Bitte versuchen Sie es erneut.', 'Objava plana nije uspjela. Pokušajte ponovno.');
			if (errorMsg.includes('Database connection unavailable')) {
				userMessage = copy('Database connection unavailable. Please start the SSH tunnel and try again.', 'Datenbankverbindung nicht verfügbar. Bitte starten Sie den SSH-Tunnel und versuchen Sie es erneut.', 'Veza s bazom nije dostupna. Pokrenite SSH tunel i pokušajte ponovno.');
			} else if (errorMsg.includes('CORS') || errorMsg.includes('Failed to fetch')) {
				userMessage = copy('Failed to connect to server. Please check your connection and try again.', 'Verbindung zum Server fehlgeschlagen. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.', 'Povezivanje sa serverom nije uspjelo. Provjerite vezu i pokušajte ponovno.');
			} else if (isDraft) {
				userMessage = copy('Saving the plan failed. Please try again.', 'Speichern des Plans fehlgeschlagen. Bitte versuchen Sie es erneut.', 'Spremanje plana nije uspjelo. Pokušajte ponovno.');
			}
			
			if (silentSave) {
				setCompanyAutoSaveStatus('failed');
			}
			if (!silentSave) {
				showAlert(userMessage, { 
					title: isDraft ? copy('Save failed', 'Speichern fehlgeschlagen', 'Spremanje nije uspjelo') : copy('Publishing failed', 'Veröffentlichung fehlgeschlagen', 'Objava nije uspjela') 
				});
			}
			setIsPersistingPlan(false);
			return; // Don't try localStorage - show error instead
		}
	} else {
		console.warn('⚠️ Plan NOT being saved to database because:', {
			isNumericUserId,
			hasUserId: userIdNum !== undefined,
			hasUser: !!user,
			userId: userId,
			status,
			reason: !user ? 'User not logged in' : !isNumericUserId ? 'User ID is not numeric' : 'Unknown reason'
		});
	}

	// Only save to localStorage if database save didn't succeed (demo mode)
	const plan: PublicPlan = {
		id,
		databaseId,
		seekerUserId: userId ? String(userId) : 'u-seeker-demo',
		title: `${stylePresetsByKey[questionnaire.styleKey]?.title} Garden - ${questionnaire.location}`,
		city: questionnaire.location,
		contactEmail: effectiveContact,
		styleKey: questionnaire.styleKey,
		// Use uploaded URL if available, otherwise only use data URL if not saving to database
		photoBeforeUrl: uploadedPhotoUrl || (isNumericUserId ? undefined : photoUrlForStorage),
		status,
		createdAt: new Date().toISOString(),
		questionnaire: optimizedQuestionnaire,
		aiGenerated: aiContent
	};

	try {
		pruneDemoPlans(status === 'draft' ? 4 : 3);
		addPublicPlan(plan);
		localStorage.removeItem('pendingQuestionnaire');
		localStorage.removeItem('gardenQuestionnaireDraft');
		if (!options?.silent) {
			showAlert(successMessage, { title: successTitle });
		} else {
			setCompanyAutoSaveStatus('saved');
		}
		const planIdForNavigation = databaseId ? `db-${databaseId}` : id;
		const navigateTo = options?.navigateTo ?? 'plan';
		setIsPersistingPlan(false);
		if (navigateTo === 'offer') {
			navigate(`/app/plans/${planIdForNavigation}`, {
				state: { returnTo: '/app/seeker/generate-plan' }
			});
		} else if (navigateTo !== 'none') {
			navigate(`/app/seeker/plan/${planIdForNavigation}`);
		}
	} catch (err) {
		console.error('Failed to persist plan', err);
		const isDraft = status === 'draft';
		const quotaExceeded =
			err instanceof DOMException &&
			(err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED');
		const message = quotaExceeded
			? copy('Saving failed because the demo storage is full. Remove older plans or use a smaller photo, then try again.', 'Speichern fehlgeschlagen, weil der Demo-Speicher voll ist. Entfernen Sie ältere Pläne oder verwenden Sie ein kleineres Foto und versuchen Sie es erneut.', 'Spremanje nije uspjelo jer je demo pohrana puna. Uklonite starije planove ili koristite manju fotografiju pa pokušajte ponovno.')
			: isDraft
				? copy('Saving the plan failed. Please try again.', 'Speichern des Plans fehlgeschlagen. Bitte versuchen Sie es erneut.', 'Spremanje plana nije uspjelo. Pokušajte ponovno.')
				: copy('Publishing the plan failed. Please try again.', 'Die Veröffentlichung des Plans ist fehlgeschlagen. Bitte versuchen Sie es erneut.', 'Objava plana nije uspjela. Pokušajte ponovno.');
		if (quotaExceeded) {
			try {
				pruneDemoPlans(isDraft ? 2 : 1);
				addPublicPlan(plan);
				localStorage.removeItem('pendingQuestionnaire');
				localStorage.removeItem('gardenQuestionnaireDraft');
				if (!options?.silent) {
					showAlert(successMessage, { title: successTitle });
				} else {
					setCompanyAutoSaveStatus('saved');
				}
				const planIdForNavigation = databaseId ? `db-${databaseId}` : id;
				const navigateTo = options?.navigateTo ?? 'plan';
				setIsPersistingPlan(false);
				if (navigateTo === 'offer') {
					navigate(`/app/plans/${planIdForNavigation}`, {
						state: { returnTo: '/app/seeker/generate-plan' }
					});
				} else if (navigateTo !== 'none') {
					navigate(`/app/seeker/plan/${planIdForNavigation}`);
				}
				return;
			} catch (retryErr) {
				console.error('Retry after pruning demo plans failed', retryErr);
			}
		}
		if (silentSave) {
			setCompanyAutoSaveStatus('failed');
		}
		if (!silentSave) {
			showAlert(message, { title: isDraft ? copy('Save failed', 'Speichern fehlgeschlagen', 'Spremanje nije uspjelo') : copy('Publishing failed', 'Veröffentlichung fehlgeschlagen', 'Objava nije uspjela') });
		}
		setIsPersistingPlan(false);
	}
	};

	const queued = persistPlanQueueRef.current.catch(() => {}).then(runPersist);
	persistPlanQueueRef.current = queued;
	await queued;
}

function handleSaveDraft() {
	if (isCompanyFlow) {
		void handleCompanySavePlan();
		return;
	}
	void persistPlan(
		'draft',
		copy('Plan saved as draft!', 'Plan als Entwurf gespeichert!', 'Plan je spremljen kao skica!'),
		copy('Draft saved', 'Entwurf gespeichert', 'Skica spremljena')
	);
}

function handleCompanySavePlan() {
	void persistPlan(
		'draft',
		copy('Plan saved to Direct plans.', 'Plan in Direkte Pläne gespeichert.', 'Plan je spremljen u Izravne planove.'),
		copy('Plan saved', 'Plan gespeichert', 'Plan spremljen'),
		{ navigateTo: 'plan' }
	);
}

async function handleContinueToQuote() {
	// Wait for in-flight auto-save so we don't create a second row.
	await persistPlanQueueRef.current.catch(() => {});
	const existingId = resolveSavedDatabaseId();
	if (existingId != null) {
		navigate(`/app/plans/db-${existingId}`, {
			state: { returnTo: '/app/seeker/generate-plan' }
		});
		return;
	}
	await persistPlan(
		'draft',
		copy('Opening quote builder…', 'Angebotserstellung wird geöffnet…', 'Otvaranje izrade ponude…'),
		copy('Continue to quote', 'Weiter zum Angebot', 'Nastavi na ponudu'),
		{ navigateTo: 'offer' }
	);
}

useEffect(() => {
	if (loading || !aiContent || !questionnaire || error) return;
	if (companyAutoSaveAttemptedRef.current) return;
	const email = (contactEmail || user?.email || '').trim();
	if (!email) return;

	companyAutoSaveAttemptedRef.current = true;
	setCompanyAutoSaveStatus('saving');
	void persistPlan('draft', '', '', { navigateTo: 'none', silent: true });
	// eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot auto-save after generation (updates existing draft if present)
}, [loading, aiContent, questionnaire, error, contactEmail, user?.email]);

	async function handleDownloadPDF() {
		if (!questionnaire || !aiContent) {
			showAlert(copy('Plan data is not available for PDF generation.', 'Plandaten sind für die PDF-Erstellung nicht verfügbar.', 'Podaci plana nisu dostupni za generiranje PDF-a.'), { title: copy('Error', 'Fehler', 'Greška') });
			return;
		}

		try {
			// Create a temporary plan object for PDF generation
			const tempPlan: PublicPlan = {
				id: `temp-${Date.now()}`,
				seekerUserId: user?.id ? String(user.id) : 'temp',
				title: `${stylePresetsByKey[questionnaire.styleKey]?.title} Garden - ${questionnaire.location}`,
				city: questionnaire.location,
				contactEmail: (contactEmail || user?.email || '').trim() || '',
				styleKey: questionnaire.styleKey,
				photoBeforeUrl: questionnaire.photoBeforeUrl,
				status: 'draft',
				createdAt: new Date().toISOString(),
				questionnaire,
				aiGenerated: aiContent
			};

			await generatePlanPDF({
				plan: tempPlan,
				aiContent,
				questionnaire,
				language
			});
		} catch (error) {
			console.error('Failed to generate PDF:', error);
			showAlert(copy('Failed to generate PDF. Please try again.', 'PDF konnte nicht erstellt werden. Bitte versuchen Sie es erneut.', 'Generiranje PDF-a nije uspjelo. Pokušajte ponovno.'), { title: copy('Error', 'Fehler', 'Greška') });
		}
	}

	async function handleRegenerateWithChanges() {
		if (!questionnaire || !aiContent) return;
		const originalBeforeUrl = questionnaire.photoBeforeUrl;
		const latestAfterUrl = aiContent.photoAfterUrl;
		if (!originalBeforeUrl) {
			showAlert(copy('This plan has no before photo. Please go back and add one.', 'Dieser Plan hat kein Vorher-Foto. Bitte gehen Sie zurück und fügen Sie eines hinzu.', 'Ovaj plan nema početnu fotografiju. Vratite se i dodajte je.'), { title: copy('Cannot regenerate', 'Erneutes Generieren nicht möglich', 'Ponovno generiranje nije moguće') });
			return;
		}
		const instructions = editInstructions.trim();
		if (!instructions) {
			showAlert(copy('Please describe what you\'d like to change (e.g. different fence colour, more flowers).', 'Bitte beschreiben Sie, was Sie ändern möchten (z. B. andere Zaunfarbe, mehr Blumen).', 'Opišite što želite promijeniti (npr. druga boja ograde, više cvijeća).'), { title: copy('Add your changes', 'Änderungen hinzufügen', 'Dodajte svoje promjene') });
			return;
		}
		setIsRegenerating(true);
		try {
			const q = { ...questionnaire, photoBeforeUrl: originalBeforeUrl };
			const newContent = await generateAIPlan(q, {
				editInstructions: instructions,
				editBaseImageUrl: latestAfterUrl || originalBeforeUrl,
				originalBeforeImageUrl: originalBeforeUrl,
				existingPlan: aiContent,
				userId: user?.id,
				language:
					languageRef.current === 'de' ? 'de' : languageRef.current === 'hr' ? 'hr' : 'en'
			});
			const mergedContent = {
				...newContent,
				photoAfterUrl: newContent.photoAfterUrl || aiContent.photoAfterUrl || ''
			};
			setAiContent(mergedContent);
			if (questionnaire) {
				patchGeneratePlanReview({
					questionnaire,
					aiContent: mergedContent
				});
			}
			setEditModalOpen(false);
			setEditInstructions('');
			showAlert(copy('Plan updated with your changes.', 'Plan wurde mit Ihren Änderungen aktualisiert.', 'Plan je ažuriran prema vašim promjenama.'), { title: copy('Done', 'Gotovo', 'Gotovo') });
			if (resolveSavedDatabaseId() != null) {
				setCompanyAutoSaveStatus('saving');
				void persistPlan('draft', '', '', { navigateTo: 'none', silent: true });
			}
		} catch (err) {
			console.error('Regenerate failed:', err);
			showAlert(err instanceof Error ? err.message : copy('Failed to regenerate. Please try again.', 'Neugenerierung fehlgeschlagen. Bitte versuchen Sie es erneut.', 'Ponovno generiranje nije uspjelo. Pokušajte ponovno.'), { title: copy('Error', 'Fehler', 'Greška') });
		} finally {
			setIsRegenerating(false);
		}
	}

function handlePublish() {
	void persistPlan(
		'active',
		copy('Plan published! Gardeners can now see it and send offers.', 'Plan veröffentlicht! Gärtner können ihn jetzt sehen und Angebote senden.', 'Plan je objavljen! Vrtlari ga sada mogu vidjeti i slati ponude.'),
		copy('Plan published', 'Plan veröffentlicht', 'Plan objavljen')
	);
}

const actionsDisabled = isPersistingPlan || isRegenerating;

	const renderHeader = (title: string) => (
		<div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
			<BackButton fallbackPath="/app/seeker/create-plan" label={copy('Back to questionnaire', 'Zurück zum Fragebogen', 'Natrag na upitnik')} size="sm" />
			<h1 className="h3 mb-0">{title}</h1>
		</div>
	);

	if (loading) {
		return (
			<>
				{renderHeader(copy('Generating Your Garden Plan', 'Ihr Gartenplan wird erstellt', 'Izrađujemo vaš vrtni plan'))}
				<div className="card">
					<div className="card-body py-5" style={{ minHeight: '400px' }}>
						<PlanGenerationPuzzleAnimation
							beforeImageUrl={questionnaire?.photoBeforeUrl}
							selectedComponents={questionnaire?.selectedComponents}
							subMessage={copy("We're analysing your garden and creating a personalised plan. This usually takes about 1-3 minutes. You can switch to another tab if you like-just keep this page open and we'll keep working.", 'Wir analysieren Ihren Garten und erstellen einen personalisierten Plan. Das dauert in der Regel 1-3 Minuten. Sie können gern zu einem anderen Tab wechseln - lassen Sie diese Seite einfach geöffnet.', 'Analiziramo vaš vrt i izrađujemo personalizirani plan. To obično traje 1-3 minute. Slobodno otvorite drugu karticu - samo ostavite ovu stranicu otvorenom.')}
						/>
						<div className="d-flex justify-content-center mt-3">
							<div className="badge rounded-pill text-bg-light border px-3 py-2">
								<i className="bi bi-clock-history me-2" aria-hidden="true"></i>
								{copy('Elapsed time', 'Verstrichene Zeit', 'Proteklo vrijeme')}: {formattedGenerationElapsed}
							</div>
						</div>
					</div>
				</div>
			</>
		);
	}

	if (error || !questionnaire || !aiContent) {
		const displayedError =
			error && questionnaire
				? `${copy(
						'This is a temporary service issue, not a problem with your answers.',
						'Dies ist ein vorübergehendes Problem des Dienstes — nicht mit Ihren Angaben im Fragebogen.',
						'Ovo je privremeni problem usluge, a ne problem s vašim odgovorima u upitniku.'
					)} ${error}`
				: error ||
						copy(
							'Failed to load plan data.',
							'Plandaten konnten nicht geladen werden.',
							'Učitavanje podataka o planu nije uspjelo.'
						);
		return (
			<>
				{renderHeader(copy('Something went wrong', 'Etwas ist schiefgegangen', 'Došlo je do problema'))}
				<div className="alert alert-danger">
					<p className="mb-2">{displayedError}</p>
					{questionnaire && (
						<p className="mb-0 small">
							{copy('Your questionnaire answers are still available, so you can try again now or save them for later.', 'Ihre Angaben aus dem Fragebogen sind weiterhin vorhanden. Sie können es jetzt erneut versuchen oder sie für später speichern.', 'Vaši odgovori iz upitnika i dalje su spremljeni, pa možete pokušati ponovno sada ili ih spremiti za kasnije.')}
						</p>
					)}
					<div className="mt-3 d-flex flex-wrap gap-2">
						<button className="btn btn-primary" onClick={handleRetryGeneration}>
							{copy('Try Again', 'Erneut versuchen', 'Pokušaj ponovno')}
						</button>
						<button className="btn btn-outline-secondary" onClick={handleSaveQuestionnaireDraft}>
							{copy('Save Questionnaire Draft', 'Fragebogen als Entwurf speichern', 'Spremi upitnik kao skicu')}
						</button>
						<button className="btn btn-outline-primary" onClick={() => navigate('/app/seeker/create-plan')}>
							{copy('Back to Questionnaire', 'Zurück zum Fragebogen', 'Natrag na upitnik')}
						</button>
					</div>
				</div>
			</>
		);
	}

	const resolvedAi = displayAi as AIGeneratedContent;
	const style = stylePresetsByKey[questionnaire.styleKey];
	const safePlanDescription = toText(
		resolvedAi?.planDescription,
		copy('Your plan was generated successfully.', 'Ihr Plan wurde erfolgreich erstellt.', 'Vaš plan je uspješno generiran.')
	);
	const safeKeyFeatures = toTextList(resolvedAi?.keyFeatures);
	const safeInstructions = toTextList(resolvedAi?.instructions);
	const safeRequiredSkills = toTextList(resolvedAi?.required_skills);
	const componentCoverageText = buildComponentCoverageText(resolvedAi);
	const rawMissingSelectedComponents = Array.isArray(resolvedAi?.selected_component_coverage?.missing)
		? resolvedAi.selected_component_coverage!.missing
				.map((id) => String(id).trim())
				.filter((id) => id.length > 0)
		: [];
	const missingSelectedComponents = rawMissingSelectedComponents.filter(
		(componentId) => !hasComponentTextEvidence(componentId, componentCoverageText)
	);
	const rawIncludedSelectedComponents = Array.isArray(resolvedAi?.selected_component_coverage?.included)
		? resolvedAi.selected_component_coverage!.included
				.map((id) => String(id).trim())
				.filter((id) => id.length > 0)
		: [];
	const includedSelectedComponents = uniqueStrings([
		...rawIncludedSelectedComponents,
		...(questionnaire.selectedComponents ?? []).filter(
			(componentId) =>
				hasComponentTextEvidence(componentId, componentCoverageText) &&
				!missingSelectedComponents.includes(componentId)
		)
	]);
	const safeTools =
		Array.isArray(resolvedAi?.tools) && resolvedAi.tools.length > 0
			? typeof resolvedAi.tools[0] === 'string'
				? toTextList(resolvedAi.tools)
				: (resolvedAi.tools as Array<{ name: unknown; description: unknown }>)
						.map((tool) => ({
							name: toText(tool?.name),
							description: toText(tool?.description)
						}))
						.filter((tool) => tool.name.length > 0)
			: [];
	const pricingBreakdown = resolvedAi?.pricingBreakdown;
	const companyDetailedTools =
		canEditCompanyPlan && editablePlanContent?.tools && Array.isArray(editablePlanContent.tools)
			? editablePlanContent.tools
			: safeTools;
	const companyDetailedMaterials =
		canEditCompanyPlan && editablePlanContent?.pricingBreakdown?.materials && Array.isArray(editablePlanContent.pricingBreakdown.materials)
			? editablePlanContent.pricingBreakdown.materials
			: pricingBreakdown?.materials ?? [];
	const companyShareVisibility = normalizeShareVisibility(companyInviteStatus?.share_visibility);
	return (
		<>
			<div 
				className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 sticky-top"
				style={{
					backgroundColor: 'white',
					zIndex: 1000,
					top: '60px',
					paddingTop: '1rem',
					paddingBottom: '1rem',
					boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
				}}
			>
				<div className="d-flex align-items-center gap-2">
					<BackButton fallbackPath="/app/seeker/create-plan" label={copy('Back to questionnaire', 'Zurück zum Fragebogen', 'Natrag na upitnik')} size="sm" />
					<h1 className="h3 mb-0">
						{isCompanyFlow
							? copy('Review plan & build quote', 'Plan prüfen & Angebot erstellen', 'Pregled plana i izrada ponude')
							: copy('Your Garden Plan', 'Ihr Gartenplan', 'Vaš vrtni plan')}
					</h1>
				</div>
				<div className="d-flex flex-wrap align-items-center gap-2">
					<div className="d-flex flex-wrap gap-2">
						<button
							className="btn btn-outline-success btn-sm"
							onClick={() => setEditModalOpen(true)}
							disabled={actionsDisabled}
							title={copy('Describe changes to regenerate the plan', 'Änderungen beschreiben, um den Plan neu zu erstellen', 'Opišite izmjene za ponovno generiranje plana')}
						>
							<i className="bi bi-pencil-square me-1"></i>{copy('Change something?', 'Etwas ändern?', 'Želite li nešto promijeniti?')}
						</button>
						{isCompanyFlow ? (
							<>
								<button
									className="btn btn-outline-secondary"
									onClick={handleCompanySavePlan}
									disabled={actionsDisabled}
									title={copy('Save to Direct plans', 'In Direkte Pläne speichern', 'Spremi u Izravne planove')}
								>
									<i className="bi bi-save me-2"></i>
									{isPersistingPlan ? copy('Saving…', 'Speichern…', 'Spremanje…') : copy('Save plan', 'Plan speichern', 'Spremi plan')}
								</button>
								<button
									className="btn btn-success"
									onClick={handleContinueToQuote}
									disabled={actionsDisabled}
									title={copy('Edit plan details and build your client quote', 'Plandetails bearbeiten und Kundenangebot erstellen', 'Uredite plan i izradite ponudu za klijenta')}
								>
									<i className="bi bi-receipt me-2"></i>
									{isPersistingPlan ? copy('Opening…', 'Öffnen…', 'Otvaranje…') : copy('Continue to quote', 'Weiter zum Angebot', 'Nastavi na ponudu')}
								</button>
							</>
						) : (
							<>
								<button className="btn btn-outline-secondary" onClick={handleSaveDraft} disabled={actionsDisabled} title={copy('Save as Draft', 'Als Entwurf speichern', 'Spremi kao skicu')}>
									<i className="bi bi-save me-2"></i>
									{isPersistingPlan
										? copy('Saving…', 'Speichern…', 'Spremanje…')
										: planSaved
											? copy('Update draft', 'Entwurf aktualisieren', 'Ažuriraj skicu')
											: copy('Save as Draft', 'Als Entwurf speichern', 'Spremi kao skicu')}
								</button>
								<button className="btn btn-success" onClick={handlePublish} disabled={actionsDisabled} title={copy('Publish Plan', 'Plan veröffentlichen', 'Objavi plan')}>
									<i className="bi bi-send me-2"></i>{copy('Publish Plan', 'Plan veröffentlichen', 'Objavi plan')}
								</button>
							</>
						)}
						<button className="btn btn-outline-success" onClick={handleDownloadPDF} disabled={actionsDisabled} title={copy('Download PDF', 'PDF herunterladen', 'Preuzmi PDF')}>
							<i className="bi bi-download me-2"></i>{copy('Download PDF', 'PDF herunterladen', 'Preuzmi PDF')}
						</button>
					</div>
					<button 
						className="btn btn-outline-success btn-sm" 
						onClick={() => setShowActionsInfo(!showActionsInfo)}
						title={copy('Learn more about these actions', 'Mehr über diese Aktionen erfahren', 'Saznajte više o ovim radnjama')}
						style={{ minWidth: '40px' }}
					>
						<i className={`bi ${showActionsInfo ? 'bi-x-lg' : 'bi-info-circle'}`}></i>
					</button>
				</div>
			</div>

			{companyAutoSaveStatus !== 'idle' && (
				<div
					className={`alert mb-4 border-0 shadow-sm ${
						companyAutoSaveStatus === 'failed' ? 'alert-warning' : 'alert-success'
					}`}
				>
					{companyAutoSaveStatus === 'saving' && (
						<>
							<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
							{isCompanyFlow
								? copy('Saving your plan to Direct plans…', 'Plan wird in Direkte Pläne gespeichert…', 'Spremanje plana u Izravne planove…')
								: copy('Saving your plan as a draft…', 'Plan wird als Entwurf gespeichert…', 'Spremanje plana kao skica…')}
						</>
					)}
					{companyAutoSaveStatus === 'saved' && (
						<>
							<i className="bi bi-check-circle-fill me-2" />
							{isCompanyFlow
								? copy(
									'Plan saved as a draft. Tweak the design here, then continue to build your client quote — it stays private, not on the public marketplace.',
									'Plan als Entwurf gespeichert. Passen Sie das Design an und erstellen Sie Ihr Kundenangebot — privat, ohne Marktplatz-Eintrag.',
									'Plan je spremljen kao skica. Prilagodite dizajn, zatim izradite ponudu za klijenta — ostaje privatno, bez javne tržnice.'
								)
								: copy(
									'Plan saved as a draft in My Plans. You can keep editing here or publish when you\'re ready.',
									'Plan als Entwurf unter „Meine Pläne“ gespeichert. Sie können hier weiter bearbeiten oder veröffentlichen, wenn Sie bereit sind.',
									'Plan je spremljen kao skica u „Mojim planovima“. Možete nastaviti uređivati ovdje ili objaviti kada ste spremni.'
								)}
						</>
					)}
					{companyAutoSaveStatus === 'failed' && (
						<>
							<i className="bi bi-exclamation-triangle-fill me-2" />
							{isCompanyFlow
								? copy(
									'Auto-save failed. Use Save plan before leaving, or check your connection.',
									'Automatisches Speichern fehlgeschlagen. Nutzen Sie „Plan speichern“, bevor Sie die Seite verlassen.',
									'Automatsko spremanje nije uspjelo. Koristite „Spremi plan“ prije odlaska.'
								)
								: copy(
									'Auto-save failed. Use Save as Draft before leaving, or check your connection.',
									'Automatisches Speichern fehlgeschlagen. Nutzen Sie „Als Entwurf speichern“, bevor Sie die Seite verlassen.',
									'Automatsko spremanje nije uspjelo. Koristite „Spremi kao skicu“ prije odlaska.'
								)}
						</>
					)}
				</div>
			)}

			{isCompanyFlow && companyUserId != null && savedPlanDatabaseId != null && (
				<div className="card mb-4 border-success-subtle">
					<div className="card-body">
						<div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
							<div>
								<div className="fw-semibold mb-1">
									{copy('Linked seeker visibility', 'Sichtbarkeit für verknüpften Suchenden', 'Vidljivost za povezanog vlasnika')}
								</div>
								<div className="text-muted small mb-2">
									{companyInviteStatus?.has_invite
										? copy(
											'These settings control which plan sections the linked seeker sees after opening their invite link.',
											'Diese Einstellungen steuern, welche Planbereiche der verknüpfte Suchende nach dem Öffnen seines Einladungslinks sieht.',
											'Ove postavke određuju koje dijelove plana povezani vlasnik vidi nakon otvaranja poveznice pozivnice.'
										)
										: copy(
											'Choose what the seeker will see when you create their invite link.',
											'Wählen Sie aus, was der Suchende sehen wird, wenn Sie seinen Einladungslink erstellen.',
											'Odaberite što će vlasnik vidjeti kada kreirate poveznicu pozivnice.'
										)}
								</div>
								<div className="d-flex flex-wrap gap-2 small">
									<span className="badge text-bg-light border">
										{copy('Pricing', 'Preise', 'Cijene')}: {companyShareVisibility.pricing === 'full'
											? copy('full', 'voll', 'puno')
											: companyShareVisibility.pricing === 'total_only'
												? copy('total only', 'samo ukupno', 'samo ukupno')
												: copy('hidden', 'versteckt', 'skriveno')}
									</span>
									<span className={`badge border ${companyShareVisibility.timeline ? 'text-bg-success' : 'text-bg-light'}`}>
										{copy('Timeline', 'Zeitplan', 'Vremenski plan')}
									</span>
									<span className={`badge border ${companyShareVisibility.instructions ? 'text-bg-success' : 'text-bg-light'}`}>
										{copy('Instructions', 'Upute', 'Upute')}
									</span>
									<span className={`badge border ${companyShareVisibility.team ? 'text-bg-success' : 'text-bg-light'}`}>
										{copy('Team', 'Team', 'Tim')}
									</span>
								</div>
							</div>
							<button
								type="button"
								className="btn btn-outline-success"
								onClick={() => setShareClientModalOpen(true)}
							>
								<i className="bi bi-eye me-2" aria-hidden />
								{companyInviteStatus?.has_invite
									? copy('Update seeker view', 'Ansicht für Suchenden aktualisieren', 'Ažuriraj prikaz vlasnika')
									: copy('Set seeker view', 'Ansicht für Suchenden festlegen', 'Postavi prikaz vlasnika')}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Actions Info Card */}
			{showActionsInfo && (
				<div className="alert mb-4 border-0 shadow-sm" style={{ backgroundColor: '#d1e7dd', borderLeft: '4px solid #198754' }}>
					<div className="d-flex align-items-start">
						<i className="bi bi-info-circle-fill text-success me-2 mt-1"></i>
						<div className="flex-grow-1">
							<h6 className="fw-bold mb-2 text-success">{copy('About These Actions:', 'Zu diesen Aktionen:', 'O ovim radnjama:')}</h6>
							<ul className="mb-0 small" style={{ lineHeight: '1.8' }}>
								<li className="mb-2">
									<strong className="text-success">{copy('Change something?:', 'Etwas ändern?:', 'Želite li nešto promijeniti?:')}</strong> {copy("Describe what you'd like to change and we'll regenerate the plan-no need to save first.", 'Beschreiben Sie Ihre gewünschten Änderungen und wir erstellen den Plan neu - ein vorheriges Speichern ist nicht nötig.', 'Opišite što želite promijeniti i ponovno ćemo izraditi plan - nije potrebno prvo spremati.')}
								</li>
								{isCompanyFlow ? (
									<>
										<li className="mb-2">
											<strong className="text-success">{copy('Save plan:', 'Plan speichern:', 'Spremi plan:')}</strong>{' '}
											{copy(
												'Stores the plan in Direct plans. Invite a client or return later.',
												'Speichert den Plan unter Direkte Pläne. Laden Sie einen Kunden ein oder kehren Sie später zurück.',
												'Spremite plan u Izravne planove. Pozovite klijenta ili se vratite kasnije.'
											)}
										</li>
										<li className="mb-2">
											<strong className="text-success">{copy('Continue to quote:', 'Weiter zum Angebot:', 'Nastavi na ponudu:')}</strong>{' '}
											{copy(
												'Set pricing, team, and timeline. Sending the offer shares the plan with your client.',
												'Preis, Team und Zeitplan festlegen. Mit dem Versand teilen Sie den Plan mit Ihrem Kunden.',
												'Postavite cijenu, tim i rokove. Slanjem ponude dijelite plan s klijentom.'
											)}
										</li>
									</>
								) : (
									<li className="mb-2">
										<strong className="text-success">{copy('Save as Draft:', 'Als Entwurf speichern:', 'Spremi kao skicu:')}</strong>{' '}
										{copy(
											'Your plan is saved automatically after generation. Use this if you make changes and want to update the draft before publishing.',
											'Ihr Plan wird nach der Erstellung automatisch gespeichert. Nutzen Sie dies, wenn Sie Änderungen vorgenommen haben und den Entwurf vor der Veröffentlichung aktualisieren möchten.',
											'Plan se automatski sprema nakon generiranja. Koristite ovo ako ste napravili promjene i želite ažurirati skicu prije objave.'
										)}
									</li>
								)}
								<li className="mb-2">
									<strong className="text-success">{copy('Download PDF:', 'PDF herunterladen:', 'Preuzmi PDF:')}</strong>{' '}
									{copy(
										'Get a printable version of your complete garden plan with all details, instructions, and pricing.',
										'Erhalten Sie eine druckbare Version Ihres vollständigen Gartenplans mit allen Details, Anleitungen und Preisen.',
										'Preuzmite verziju cijelog vrtnog plana za ispis sa svim detaljima, uputama i cijenama.'
									)}
								</li>
								{!isCompanyFlow && (
									<li className="mb-0">
										<strong className="text-success">{copy('Publish Plan:', 'Plan veröffentlichen:', 'Objavi plan:')}</strong>{' '}
										{copy(
											'Make your plan visible to gardeners who can send you offers. Once published, gardeners can view your plan and submit quotes.',
											'Machen Sie Ihren Plan für Gärtner sichtbar, die Ihnen Angebote senden können. Nach der Veröffentlichung können Gärtner Ihren Plan ansehen und Angebote abgeben.',
											'Učinite svoj plan vidljivim vrtlarima koji vam mogu slati ponude. Nakon objave vrtlari mogu pregledati plan i poslati svoju ponudu.'
										)}
									</li>
								)}
							</ul>
						</div>
					</div>
				</div>
			)}

			<div className="row g-4">
				{/* Before/After Comparison */}
				<div className="col-12">
					<div className="card mb-4">
						<div className="card-header">
							<h5 className="mb-0">
								<i className="bi bi-images text-success me-2"></i>{copy('Before & After', 'Vorher & Nachher', 'Prije i poslije')}
							</h5>
						</div>
						<div className="card-body p-3">
							<div className="row g-3">
								<div className="col-12 col-md-6">
									<div className="text-muted small mb-2 fw-semibold">Current State</div>
									{questionnaire.photoBeforeUrl ? (
										<div
											style={{
												width: '100%',
												height: '400px',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												backgroundColor: '#f8f9fa',
												borderRadius: '0.375rem',
												cursor: 'pointer',
												overflow: 'hidden'
											}}
											onClick={() => {
												const afterImageUrl = aiContent?.photoAfterUrl;
												const isPlaceholder = afterImageUrl?.includes('unsplash.com');
												const hasAfterImage = afterImageUrl && !isPlaceholder;
												setImageModalIndex(0);
												setImageModalOpen(true);
											}}
										>
											<img
												src={getImageUrl(questionnaire.photoBeforeUrl)}
												alt="Before"
												className="img-fluid rounded shadow-sm"
												style={{
													maxWidth: '100%',
													maxHeight: '100%',
													objectFit: 'contain',
													pointerEvents: 'none'
												}}
												onError={(e) => {
													const imageSrc = getImageUrl(questionnaire.photoBeforeUrl);
													console.error('❌ Failed to load before image');
													console.error('❌ Original URL:', questionnaire.photoBeforeUrl);
													console.error('❌ Converted URL:', imageSrc);
													console.error('❌ URL length:', imageSrc?.length || 0);
												}}
											/>
										</div>
									) : (
										<div className="border rounded d-flex align-items-center justify-content-center bg-light" style={{ height: '400px' }}>
											<span className="text-muted">No photo provided</span>
										</div>
									)}
									<small className="text-muted d-block mt-2">
										<i className="bi bi-zoom-in me-1"></i>Click to expand
									</small>
								</div>
								<div className="col-12 col-md-6">
									<div className="text-muted small mb-2 fw-semibold">Your vision</div>
									{(() => {
										// Check if photoAfterUrl is a placeholder (Unsplash) or a real AI-generated image
										const afterImageUrl = aiContent?.photoAfterUrl;
										const isPlaceholder = afterImageUrl?.includes('unsplash.com');
										
										// If we have a real AI-generated image (not a placeholder), use it
										if (afterImageUrl && !isPlaceholder) {
											return (
												<>
													<div
														style={{
															width: '100%',
															height: '400px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															backgroundColor: '#f8f9fa',
															borderRadius: '0.375rem',
															cursor: 'pointer',
															overflow: 'hidden',
															border: '2px solid #198754'
														}}
														onClick={() => {
															setImageModalIndex(1);
															setImageModalOpen(true);
														}}
													>
														<img
															src={getImageUrl(afterImageUrl)}
															alt="After - Your vision"
															className="img-fluid rounded shadow-sm"
															style={{
																maxWidth: '100%',
																maxHeight: '100%',
																objectFit: 'contain',
																pointerEvents: 'none'
															}}
														/>
													</div>
													<small className="text-muted d-block mt-2">
														<i className="bi bi-zoom-in me-1"></i>Click to expand
													</small>
												</>
											);
										}
										
										// Otherwise, use the uploaded photo for both sides until we have a working AI model
										if (questionnaire.photoBeforeUrl) {
											return (
												<>
													<div
														style={{
															width: '100%',
															height: '400px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															backgroundColor: '#f8f9fa',
															borderRadius: '0.375rem',
															cursor: 'pointer',
															overflow: 'hidden',
															border: '2px solid #198754'
														}}
														onClick={() => {
															setImageModalIndex(0);
															setImageModalOpen(true);
														}}
													>
														<img
															src={getImageUrl(questionnaire.photoBeforeUrl)}
															alt="After - Your vision (placeholder)"
															className="img-fluid rounded shadow-sm"
															style={{
																maxWidth: '100%',
																maxHeight: '100%',
																objectFit: 'contain',
																pointerEvents: 'none'
															}}
															onError={(e) => {
																const imageSrc = getImageUrl(questionnaire.photoBeforeUrl);
																console.error('❌ Failed to load after image');
																console.error('❌ Original URL:', questionnaire.photoBeforeUrl);
																console.error('❌ Converted URL:', imageSrc);
																console.error('❌ URL length:', imageSrc?.length || 0);
															}}
														/>
													</div>
													<small className="text-muted d-block mt-2">
														<i className="bi bi-zoom-in me-1"></i>Click to expand
													</small>
												</>
											);
										}
										
										// No image available
										return (
											<div className="border rounded d-flex align-items-center justify-content-center bg-light" style={{ height: '400px' }}>
												<span className="text-muted">No photo provided</span>
											</div>
										);
									})()}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="row g-4">
				{/* Plan Details */}
				<div className={canEditCompanyPlan ? 'col-12' : 'col-12 col-lg-8'}>
					{canEditCompanyPlan && editablePlanContent ? (
						<>
							<div className="card mb-4">
								<div className="card-body p-0">
									<div className="px-3 pt-3 pb-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
										<p className="text-muted small mb-0">
											{copy(
												'Edit any tab while editing is on, then save once.',
												'Bearbeiten Sie beliebige Tabs im Bearbeitungsmodus und speichern Sie einmal.',
												'Uredite bilo koju karticu dok je uređivanje uključeno, zatim jednom spremite.'
											)}
										</p>
										<PlanEditToolbar
											copy={copy}
											isEditing={isEditingPlan}
											onToggleEdit={() => setIsEditingPlan((v) => !v)}
											onDiscard={handleDiscardCompanyPlanChanges}
											onSave={handleSaveCompanyPlanText}
											isSaving={isSavingPlanText}
										/>
									</div>
									<ul className="nav nav-tabs px-3" role="tablist" style={{ borderBottomColor: '#a7f3d0', borderBottomWidth: '2px' }}>
										{(['conceptual', 'detailed', 'project', 'questionnaire'] as const).map((tab) => (
											<li key={tab} className="nav-item" role="presentation">
												<button
													type="button"
													className={`nav-link ${activeTab === tab ? 'active' : ''}`}
													onClick={() => setActiveTab(tab)}
													role="tab"
													style={getTabStyle(tab)}
												>
													{tab === 'conceptual'
														? copy('Conceptual Plan', 'Konzeptplan', 'Konceptualni plan')
														: tab === 'detailed'
															? copy('Project Plan', 'Projektplan', 'Projektni plan')
															: tab === 'project'
																? copy('Detailed Plan', 'Detaillierter Plan', 'Detaljni plan')
																: copy('Client Inputs', 'Kundeneingaben', 'Unos klijenta')}
												</button>
											</li>
										))}
									</ul>
								</div>
							</div>

							{activeTab === 'conceptual' && (
								<>
									<div className="card mb-4">
										<div className="card-header">
											<h5 className="mb-0">{copy('Plan Overview', 'Planübersicht', 'Pregled plana')}</h5>
										</div>
										<div className="card-body">
											<div className="row g-3 mb-3">
												<div className="col-6 col-md-4">
													<div className="text-muted small mb-1">{copy('Style', 'Stil', 'Stil')}</div>
													<div className="fw-bold text-success">{style?.title}</div>
												</div>
												<div className="col-6 col-md-4">
													<div className="text-muted small mb-1">{copy('Garden Size', 'Gartengröße', 'Veličina vrta')}</div>
													<div className="fw-bold">{questionnaire.gardenSize} m²</div>
												</div>
												<div className="col-12 col-md-4">
													<div className="text-muted small mb-1">{copy('Location', 'Standort', 'Lokacija')}</div>
													<div className="fw-bold">{questionnaire.location}</div>
												</div>
											</div>
											{questionnaire.selectedComponents && questionnaire.selectedComponents.length > 0 && (
												<div className="d-flex flex-wrap gap-2">
													{questionnaire.selectedComponents.map((componentId) => (
														<span key={componentId} className="badge bg-success text-white">
															{getComponentLabel(componentId, language)}
														</span>
													))}
												</div>
											)}
										</div>
									</div>
									<PlanTextEditor
										content={editablePlanContent}
										onContentChange={setEditablePlanContent}
										copy={copy}
										editable
										sections={['description', 'features']}
										isEditing={isEditingPlan}
										showEditControls={false}
									/>
								</>
							)}

							{activeTab === 'detailed' && (
								<>
									<PlanTextEditor
										content={editablePlanContent}
										onContentChange={setEditablePlanContent}
										copy={copy}
										editable
										sections={['instructions']}
										isEditing={isEditingPlan}
										showEditControls={false}
									/>
									<PlanMaterialsQuantitiesCard
										materials={companyDetailedMaterials}
										copy={copy}
										editable
										isEditing={isEditingPlan}
										onChange={(nextMaterials) =>
											setEditablePlanContent((prev) => {
												if (!prev) return prev;
												return {
													...prev,
													pricingBreakdown: {
														...prev.pricingBreakdown,
														materials: nextMaterials
													}
												};
											})
										}
									/>
									{companyDetailedTools.length > 0 || isEditingPlan ? (
										<div className="card mb-4">
											<div className="card-header">
												<h5 className="mb-0">
													<i className="bi bi-tools me-2"></i>
													{copy('Required Tools', 'Benötigte Werkzeuge', 'Potrebni alati')}
												</h5>
											</div>
											<div className="card-body">
												<PlanRequiredToolsList
													tools={
														typeof companyDetailedTools[0] === 'string'
															? (companyDetailedTools as string[])
															: (companyDetailedTools as Array<{ name: string; description: string }>)
													}
													editable
													isEditing={isEditingPlan}
													onChange={(nextTools) =>
														setEditablePlanContent((prev) => {
															if (!prev) return prev;
															return { ...prev, tools: nextTools as AIGeneratedContent['tools'] };
														})
													}
												/>
											</div>
										</div>
									) : null}
								</>
							)}

							{activeTab === 'project' && (
								<>
									<PlanTeamSkillsCard
										recommendedTeamSize={resolvedAi.recommended_team_size}
										requiredSkills={safeRequiredSkills}
										copy={copy}
										language={language}
									/>
									{isEditingPlan ? (
										<PlanTextEditor
											content={editablePlanContent}
											onContentChange={setEditablePlanContent}
											copy={copy}
											editable
											sections={['pricing']}
											isEditing={isEditingPlan}
											showEditControls={false}
										/>
									) : pricingBreakdown ? (
										<PlanIndicativePricingCard pricing={pricingBreakdown} copy={copy} showDisclaimer={false} />
									) : null}
									{questionnaire.projectStartDate && (
										<div className="card mb-4">
											<div className="card-header">
												<h5 className="mb-0">{copy('Preferred Start Date', 'Bevorzugtes Startdatum', 'Željeni datum početka')}</h5>
											</div>
											<div className="card-body">
												<strong>
													{new Date(questionnaire.projectStartDate).toLocaleDateString('en-GB', {
														day: 'numeric',
														month: 'long',
														year: 'numeric'
													})}
												</strong>
											</div>
										</div>
									)}
									{companyPlanForTimeline && (
										<div className="card mb-4">
											<div className="card-header">
												<h5 className="mb-0">
													<i className="bi bi-bar-chart-steps text-success me-2"></i>
													{copy('Estimated Work Plan (Weeks)', 'Geschätzter Arbeitsplan (Wochen)', 'Procijenjeni plan rada (tjedni)')}
												</h5>
											</div>
											<div className="card-body">
												<PlanTimelineEditor
													plan={companyPlanForTimeline}
													content={editablePlanContent}
													onContentChange={setEditablePlanContent}
													copy={copy}
													language={language}
													isEditing={isEditingPlan}
													companyUserId={companyUserId}
												/>
											</div>
										</div>
									)}
								</>
							)}

							{activeTab === 'questionnaire' && (
								<div className="card mb-4">
									<div className="card-header">
										<h5 className="mb-0">{copy('Client Inputs', 'Kundeneingaben', 'Unos klijenta')}</h5>
									</div>
									<div className="card-body">
										{questionnaire.needsWants && (
											<div className="mb-3">
												<div className="text-muted small">{copy('Needs & wants', 'Wünsche', 'Potrebe i želje')}</div>
												<p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{questionnaire.needsWants}</p>
											</div>
										)}
										{questionnaire.maintenanceLevel && (
											<div className="mb-3">
												<div className="text-muted small">{copy('Maintenance', 'Pflege', 'Održavanje')}</div>
												<p className="mb-0 text-capitalize">{questionnaire.maintenanceLevel}</p>
											</div>
										)}
										{questionnaire.budgetRange && (
											<div className="mb-0">
												<div className="text-muted small">{copy('Budget', 'Budget', 'Proračun')}</div>
												<p className="mb-0">
													{questionnaire.budgetRange.min.toLocaleString()} – {questionnaire.budgetRange.max.toLocaleString()} EUR
												</p>
											</div>
										)}
									</div>
								</div>
							)}
						</>
					) : (
						<>
							<div className="card mb-4">
								<div className="card-header">
									<h5 className="mb-0">
										<i className="bi bi-info-circle text-success me-2"></i>{copy('Plan Overview', 'Planübersicht', 'Pregled plana')}
									</h5>
								</div>
								<div className="card-body">
									<div className="row g-3 mb-3">
										<div className="col-6 col-md-4">
											<div className="text-muted small mb-1">{copy('Style', 'Stil', 'Stil')}</div>
											<div className="fw-bold text-success">{style?.title}</div>
										</div>
										<div className="col-6 col-md-4">
											<div className="text-muted small mb-1">{copy('Garden Size', 'Gartengröße', 'Veličina vrta')}</div>
											<div className="fw-bold">{questionnaire.gardenSize} m²</div>
										</div>
										<div className="col-12 col-md-4">
											<div className="text-muted small mb-1">{copy('Location', 'Standort', 'Lokacija')}</div>
											<div className="fw-bold">{questionnaire.location}</div>
										</div>
									</div>
									{questionnaire.selectedComponents && questionnaire.selectedComponents.length > 0 && (
										<>
											<hr />
											<div className="mb-3">
												<div className="text-muted small mb-2 fw-semibold">{copy('Garden Components', 'Gartenkomponente', 'Komponente vrta')}</div>
												<div className="d-flex flex-wrap gap-2">
													{questionnaire.selectedComponents.map((componentId) => (
														<span key={componentId} className="badge bg-success text-white">
															<i className="bi bi-check-circle me-1"></i>
															{getComponentLabel(componentId, language)}
														</span>
													))}
												</div>
											</div>
											{missingSelectedComponents.length > 0 && (
												<div className="alert border mb-3" style={{ backgroundColor: '#fff8e8', borderColor: '#f2d79c' }}>
													<div className="fw-semibold mb-2">
														<i className="bi bi-lightbulb me-2"></i>
														{copy(
															'Layout tip for your selected components',
															'Layout-Tipp für Ihre ausgewählten Komponenten',
															'Savjet rasporeda za odabrane komponente'
														)}
													</div>
													<div className="small mb-2">
														{copy(
															'These selected components are not clearly reflected in this generated version yet:',
															'Diese ausgewählten Komponenten sind in dieser generierten Version noch nicht klar erkennbar:',
															'Ove odabrane komponente još nisu jasno prikazane u ovoj generiranoj verziji:'
														)}
													</div>
													<div className="d-flex flex-wrap gap-2 mb-2">
														{missingSelectedComponents.map((componentId) => (
															<span key={componentId} className="badge text-bg-warning border">
																{getComponentLabel(componentId, language)}
															</span>
														))}
													</div>
													<div className="small mb-0">
														{copy(
															'Try "Change something?" and ask to place these items explicitly with realistic spacing and clear access paths.',
															'Nutzen Sie "Etwas ändern?" und bitten Sie um eine explizite Platzierung mit realistischen Abständen und klaren Zugängen.',
															'Pokušajte "Želite li nešto promijeniti?" i zatražite izričito postavljanje uz realne razmake i jasan pristup.'
														)}
													</div>
												</div>
											)}
											{includedSelectedComponents.length > 0 && (
												<div className="small text-muted mb-2">
													{copy('Clearly represented:', 'Klar dargestellt:', 'Jasno prikazano:')}{' '}
													{includedSelectedComponents.map((componentId) => getComponentLabel(componentId, language)).join(', ')}
												</div>
											)}
										</>
									)}
								</div>
							</div>

							<div className="card mb-4">
								<div className="card-body">
									<div>
										<div className="text-muted small mb-2 fw-semibold">{copy('Description', 'Beschreibung', 'Opis')}</div>
										<p className="mb-0" style={{ lineHeight: '1.6' }}>{safePlanDescription}</p>
									</div>
								</div>
							</div>

							<div className="card mb-4">
								<div className="card-header">
									<h5 className="mb-0">
										<i className="bi bi-star-fill text-success me-2"></i>{copy('Key Features', 'Hauptmerkmale', 'Ključne značajke')}
									</h5>
								</div>
								<div className="card-body">
									<ul className="list-unstyled mb-0">
										{safeKeyFeatures.map((feature, idx) => (
											<li key={idx} className="mb-3 pb-3 border-bottom border-light">
												<i className="bi bi-check-circle-fill text-success me-2"></i>
												<span style={{ lineHeight: '1.7' }}>{formatFeatureText(feature)}</span>
											</li>
										))}
									</ul>
								</div>
							</div>

							<div className="card mb-4">
								<div className="card-header">
									<h5 className="mb-0">
										<i className="bi bi-list-check text-success me-2"></i>{copy('Implementation Instructions', 'Umsetzungsanweisungen', 'Upute za izvedbu')}
									</h5>
								</div>
								<div className="card-body">
									<ol className="mb-0" style={{ paddingLeft: '1.2rem' }}>
										{safeInstructions.map((instruction, idx) => (
											<li key={idx} className="mb-3 pb-2" style={{ lineHeight: '1.8' }}>
												{formatInstruction(instruction)}
											</li>
										))}
									</ol>
								</div>
							</div>

							{estimatedTimelineTasks.length > 0 && (
								<div className="card mb-4">
									<div className="card-header">
										<h5 className="mb-0">
											<i className="bi bi-bar-chart-steps text-success me-2"></i>
											{copy('Estimated Work Plan (Weeks)', 'Geschätzter Arbeitsplan (Wochen)', 'Procijenjeni plan rada (tjedni)')}
										</h5>
									</div>
									<div className="card-body">
										<p className="small text-muted mb-3">
											{copy(
												'Rough sequence and duration preview. Final dates and assigned gardeners are set by the company in the accepted offer/project.',
												'Grobe Vorschau auf Ablauf und Dauer. Endgültige Termine und zugewiesene Gärtner legt die Firma im angenommenen Angebot/Projekt fest.',
												'Grubi prikaz redoslijeda i trajanja. Konačne datume i dodijeljene vrtlare postavlja tvrtka u prihvaćenoj ponudi/projektu.'
											)}
										</p>
										<GanttChart tasks={estimatedTimelineTasks} preview />
									</div>
								</div>
							)}
						</>
					)}

				</div>

				{/* Pricing sidebar — seeker single-page flow only (company uses tabs) */}
				{!canEditCompanyPlan && (
				<div className="col-12 col-lg-4">
					{pricingBreakdown && (
						<PlanIndicativePricingCard pricing={pricingBreakdown} copy={copy} showDisclaimer />
					)}
					<PlanTeamSkillsCard
						recommendedTeamSize={resolvedAi.recommended_team_size}
						requiredSkills={safeRequiredSkills}
						copy={copy}
						language={language}
					/>
					{safeTools.length > 0 && (
						<div className="card mb-4">
							<div className="card-header">
								<h5 className="mb-0">
									<i className="bi bi-tools me-2"></i>
									{copy('Required Tools', 'Benötigte Werkzeuge', 'Potrebni alati')}
								</h5>
							</div>
							<div className="card-body">
								<PlanRequiredToolsList
									tools={
										typeof safeTools[0] === 'string'
											? (safeTools as string[])
											: (safeTools as Array<{ name: string; description: string }>)
									}
								/>
							</div>
						</div>
					)}
				</div>
				)}
			</div>

			{/* Image Modal */}
			{(() => {
				const afterImageUrl = aiContent?.photoAfterUrl;
				const isPlaceholder = afterImageUrl?.includes('unsplash.com');
				const hasAfterImage = afterImageUrl && !isPlaceholder;
				
				const images: Array<{ url: string; alt: string; label: string }> = [];
				if (questionnaire.photoBeforeUrl) {
					const url = getImageUrl(questionnaire.photoBeforeUrl);
					if (url) {
						images.push({
							url,
							alt: 'Current State',
							label: 'Current State'
						});
					}
				}
				if (hasAfterImage && afterImageUrl) {
					const url = getImageUrl(afterImageUrl);
					if (url) {
						images.push({
							url,
							alt: 'Your vision',
							label: 'Your vision'
						});
					}
				}
				
				return (
					<ImageModal
						isOpen={imageModalOpen}
						onClose={() => setImageModalOpen(false)}
						images={images}
						initialIndex={imageModalIndex}
					/>
				);
			})()}

			{/* Change something modal */}
			{editModalOpen && (
				<div
					className="modal d-block"
					style={{
						backgroundColor: 'rgba(0,0,0,0.5)',
						position: 'fixed',
						inset: 0,
						zIndex: 20000,
						overflowY: 'auto'
					}}
					aria-modal="true"
				>
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">Change something?</h5>
								<button type="button" className="btn-close" onClick={() => !isRegenerating && setEditModalOpen(false)} aria-label="Close" />
							</div>
							{isRegenerating ? (
								<div className="modal-body py-4">
									<PlanGenerationPuzzleAnimation
										beforeImageUrl={questionnaire?.photoBeforeUrl}
										selectedComponents={questionnaire?.selectedComponents}
										subMessage={copy("We're applying your requested changes and regenerating your plan. This usually takes about 1-3 minutes.", 'Wir setzen Ihre gewünschten Änderungen um und erstellen Ihren Plan neu. Dies dauert normalerweise 1-3 Minuten.', 'Primjenjujemo vaše tražene izmjene i ponovno generiramo vaš plan. To obično traje 1-3 minute.')}
									/>
								</div>
							) : (
								<>
									<div className="modal-body">
										<p className="text-muted small mb-2">
											Describe what you'd like to change. We'll regenerate the image and plan to match—from small tweaks (e.g. fence colour) to bigger changes (e.g. different style).
										</p>
										<textarea
											className="form-control"
											rows={3}
											placeholder="e.g. different colour fence, more flowers, make it more modern, less lawn..."
											value={editInstructions}
											onChange={(e) => setEditInstructions(e.target.value)}
											disabled={isRegenerating}
										/>
									</div>
									<div className="modal-footer">
										<button type="button" className="btn btn-secondary" onClick={() => setEditModalOpen(false)} disabled={isRegenerating}>
											Cancel
										</button>
										<button type="button" className="btn btn-success" onClick={handleRegenerateWithChanges} disabled={isRegenerating || !editInstructions.trim()}>
											<>Regenerate <i className="bi bi-arrow-repeat ms-1"></i></>
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			)}
			{isCompanyFlow && companyUserId != null && savedPlanDatabaseId != null && (
				<ShareClientLinkModal
					open={shareClientModalOpen}
					onClose={() => {
						setShareClientModalOpen(false);
						void getCompanyInviteForPlan(savedPlanDatabaseId, companyUserId)
							.then((res) => setCompanyInviteStatus(res))
							.catch(() => {});
					}}
					planDatabaseId={savedPlanDatabaseId}
					companyUserId={companyUserId}
					copy={copy}
					existingInviteCode={companyInviteStatus?.code ?? null}
					existingInviteEmail={companyInviteStatus?.invited_email ?? null}
				/>
			)}
		</>
	);
}

